// ═══════════════════════════════════════════════════════════════════
// SOURCING.JS — BASE FILE  (Paste SPLIT 1 → SPLIT 6 below this)
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from 'react';
import VALibrary from './VALibrary';
import {
  Users, LayoutDashboard, BarChart2, Send, Layers, Plus, Trash2,
  Upload, MapPin, Save, X, Link as LinkIcon, Image as ImageIcon,
  Calendar as CalIcon, Search, Info, CheckSquare, Star, Phone,
  Mail, Globe, MessageCircle, Factory, ShoppingBag, Package,
  TrendingUp, Edit3, Eye, Award, Leaf, Shield, DollarSign,
  AlertTriangle, CheckCircle, FileText, Zap, ExternalLink,
  Copy, Download, RefreshCw, ChevronRight, Filter
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API_BASE = 'https://nirvikalp.integrashion.com/api';
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─────────────────────────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────
const Section = ({ title, children, actions }) => (
  <div style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
    <div style={{ background: '#f8fafc', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: 'bold', color: '#334155', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{title}</span>
      {actions && <div style={{ display: 'flex', gap: '8px' }}>{actions}</div>}
    </div>
    <div style={{ padding: '16px', background: 'white' }}>{children}</div>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', options = null, placeholder = '', style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', ...style }}>
    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px' }}>{label}</label>
    {options ? (
      <select value={value} onChange={onChange} style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', background: 'white' }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }} />
    )}
  </div>
);

const DynamicTable = ({ columns, data, setData, onAdd }) => (
  <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
      <thead>
        <tr style={{ background: '#f1f5f9' }}>
          {columns.map((c, i) => <th key={i} style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #cbd5e1', fontWeight: '600', color: '#475569' }}>{c.label}</th>)}
          <th style={{ padding: '8px', borderBottom: '1px solid #cbd5e1', width: '40px' }}>Del</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, rIdx) => (
          <tr key={rIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
            {columns.map((c, cIdx) => (
              <td key={cIdx} style={{ padding: '4px' }}>
                {c.type === 'select' ? (
                  <select value={row[c.key] || ''} onChange={e => { const d = [...data]; d[rIdx][c.key] = e.target.value; setData(d); }}
                    style={{ width: '100%', padding: '5px', border: '1px solid #e2e8f0', borderRadius: '3px' }}>
                    <option value="">-</option>
                    {c.options?.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={c.type || 'text'} value={row[c.key] || ''} onChange={e => { const d = [...data]; d[rIdx][c.key] = e.target.value; setData(d); }}
                    style={{ width: '100%', padding: '5px', border: '1px solid #e2e8f0', borderRadius: '3px', boxSizing: 'border-box' }} />
                )}
              </td>
            ))}
            <td style={{ textAlign: 'center' }}>
              <button onClick={() => setData(data.filter((_, i) => i !== rIdx))}
                style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}>
                <Trash2 size={13} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <button onClick={onAdd} style={{ marginTop: '6px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>
      <Plus size={13} /> Add Row
    </button>
  </div>
);

const StatCard = ({ label, value, sub, color = '#2563eb', icon: Icon, onClick }) => (
  <div onClick={onClick} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.2s' }}
    onMouseEnter={e => onClick && (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
    onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)')}>
    <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {Icon && <Icon size={22} color={color} />}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '26px', fontWeight: '800', color: '#1e293b', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', fontWeight: '500' }}>{label}</div>
      {sub && <div style={{ fontSize: '11px', color, marginTop: '2px', fontWeight: '600' }}>{sub}</div>}
    </div>
  </div>
);

const MiniBar = ({ label, value, max, color = '#2563eb', extra }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', marginBottom: '4px' }}>
        <span style={{ color: '#334155', fontWeight: '500' }}>{label}</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {extra && <span style={{ fontSize: '10px', color: '#94a3b8' }}>{extra}</span>}
          <span style={{ color, fontWeight: 'bold', minWidth: '28px', textAlign: 'right' }}>{value}</span>
        </div>
      </div>
      <div style={{ height: '7px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
};

const MapEmbed = ({ lat, lng, label = 'Location', height = '180px' }) => {
  if (!lat || !lng || isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) {
    return (
      <div style={{ background: '#f1f5f9', padding: '24px', textAlign: 'center', color: '#94a3b8', borderRadius: '6px', fontSize: '12px', border: '1px dashed #cbd5e1' }}>
        <MapPin size={20} style={{ marginBottom: '6px', opacity: 0.5 }} /><br />Enter lat/lng to view map
      </div>
    );
  }
  const la = parseFloat(lat), lo = parseFloat(lng);
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${lo - 0.025},${la - 0.025},${lo + 0.025},${la + 0.025}&layer=mapnik&marker=${la},${lo}`;
  return <iframe src={src} style={{ width: '100%', height, border: 'none', borderRadius: '6px' }} title={label} />;
};

const RatingStars = ({ value, onChange, max = 5 }) => (
  <div style={{ display: 'flex', gap: '3px' }}>
    {Array(max).fill(0).map((_, i) => (
      <Star key={i} size={16} fill={i < value ? '#f59e0b' : 'none'} color={i < value ? '#f59e0b' : '#cbd5e1'}
        style={{ cursor: onChange ? 'pointer' : 'default' }} onClick={() => onChange && onChange(i + 1)} />
    ))}
    <span style={{ marginLeft: '6px', fontSize: '12px', color: '#64748b', fontWeight: 'bold' }}>{value || 0}/{max}</span>
  </div>
);

const Tag = ({ label, color = '#2563eb', onRemove }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: color + '15', color, borderRadius: '12px', fontSize: '10px', fontWeight: 'bold', border: `1px solid ${color}30` }}>
    {label} {onRemove && <X size={10} style={{ cursor: 'pointer' }} onClick={onRemove} />}
  </span>
);

// ─────────────────────────────────────────────────────────────────
// INITIAL STATES
// ─────────────────────────────────────────────────────────────────
const INIT_SUP = {
  id: null, name: '', companyId: '', supplierType: 'Garment Supplier',
  address:        { city: '', state: '', country: '', zip: '', lat: '', lng: '' },
  factoryAddress: { city: '', state: '', country: '', zip: '', lat: '', lng: '' },
  taxId: '', ownership: 'Sole Proprietorship', ownershipManual: '',
  owners: [], customers: [], factories: [], productCats: [],
  contact: { phone: '', mobile: '', email: '', website: '', whatsapp: '', linkedin: '', instagram: '' },
  images:  { companyLogo: '', companyImages: [], factoryImages: [] },
  files:   { profile: '', gallery: '' },
  // Garment
  totalSewingArea: '', totalLines: 0, sewingLines: [],
  cutting:   { area: '', tables: '', length: '', cutters: '', spreaders: '', type: 'Manual', brand: '' },
  finishing: { area: '', tables: '', packers: '', capacity: '' },
  // RM
  rmType: 'Fabric',
  // VA
  vaType: 'Print',
  vaDetails: { technique: '', mode: 'Manual', sampleCap: '', prodCap: '', leadTime: '', tablesOrMachines: '', handworkers: '', umbrellaMachines: '', industrialMachines: '', washingMachines: '', dryingMachines: '', ppStations: '', laserMachines: '' },
  // Evaluation
  evaluation: { sustScore: 0, waterUsage: '', ghgEmission: '', wasteMgmt: 'Basic', socialAudit: '', certifications: [], qualityScore: 0, rejectionRate: '', lastAuditScore: '', auditDate: '', qualityCerts: [] },
  // Calendar & Calc
  calendar: Array(12).fill(null).map((_, i) => {
    const MONTH_DAYS=[31,28,31,30,31,30,31,31,30,31,30,31];
    const SUNDAYS=[4,4,4,5,4,4,4,4,5,4,4,4];
    return { month: i+1, holidays: 0, workingDays: MONTH_DAYS[i]-SUNDAYS[i] };
  }),
  totalCapacity: 0, factoryEff: 0, machineTotal: 0, operatorHelperRatio: 0,
  agreedWastage: '',  // Agreed wastage % with this supplier for ordering calculation
};

const INIT_PROD = { id: null, name: '', supplier: '', rate: '', unit: 'Mtr', fabPrice: '', cm: '', smv: '', eff: '', ohp: '', sku: '', composition: '', moq: '', leadTime: '', sampleLeadTime: '', paymentTerms: '', usp: '', image: '' };
const INIT_PO   = { id: null, poNo: '', supplier: '', productCat: '', qty: '', startDate: '', exFactoryDate: '', items: '', date: new Date().toISOString().split('T')[0], status: 'Draft' };

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Sourcing({ db, setDb, saveToDB }) {
  const TABS = ['DASHBOARD','AUDIT','REGISTRATION','PRODUCTS','SOURCING BOM','QUOTATIONS','COSTING','ALLOCATION','MARKET RATES'];
  const [activeTab, setActiveTab] = useState('DASHBOARD');

  // ── Core State ──
  const [suppliers,   setSuppliers]   = useState([]);
  const [products,    setProducts]    = useState([]);
  const [sourcingBom, setSourcingBom] = useState([]);
  const [pos,         setPos]         = useState([]);
  const [invites,     setInvites]     = useState([]);
  const [quotations,  setQuotations]  = useState([]);
  const [rfqItems,    setRfqItems]    = useState([]);
  const [savedFobs,   setSavedFobs]   = useState([]);

  // ── UI State ──
  const [hoveredSup,    setHoveredSup]    = useState(null);
  const [showProfile,   setShowProfile]   = useState(null);   // supplier obj
  const [selectedStyle, setSelectedStyle] = useState('');
  const [rfqModal,      setRfqModal]      = useState(null);   // { bomItem, matched }
  const [poModal,       setPoModal]       = useState(null);   // fob obj for PO
  const [quoteInput,    setQuoteInput]    = useState({ supplier: '', item: '', price: '', unit: '', moq: '', leadTime: '', notes: '' });
  const [filterCat,     setFilterCat]     = useState('All');

  // ── Forms ──
  const [supForm,  setSupForm]  = useState({ ...INIT_SUP });
  const [prodForm, setProdForm] = useState({ ...INIT_PROD });
  const [poForm,   setPoForm]   = useState({ ...INIT_PO });

  // ── FOB Builder State ──
  const [fobSelections, setFobSelections] = useState({});  // { itemKey: quoteObj }
  const [fobComment,    setFobComment]    = useState('');
  const [manualFob,     setManualFob]     = useState('');
  const [auditView,       setAuditView]       = useState('list');
  const [auditScores,     setAuditScores]     = useState({});
  const [auditNA,         setAuditNA]         = useState({});
  const [sectionComments, setSectionComments] = useState({});
  const [auditMeta,       setAuditMeta]       = useState({ supplierName:'', auditorName:'', auditDate:new Date().toISOString().split('T')[0], auditType:'Full Audit', overallComments:'' });
  const [viewAudit,       setViewAudit]       = useState(null);
  const [activeSection,   setActiveSection]   = useState('lab');

  // ── DB Sync ──
  useEffect(() => {
    setSuppliers(db.suppliers || []);
    setProducts(db.sourcingProducts || []);
    setSourcingBom(db.sourcingBom || []);
    const rawPos = db.allocations?.length > 0 ? db.allocations : (db.productionLogs || []);
    setPos(rawPos.filter(l => l && typeof l === 'object' && l.type === 'ALLOCATION'));
    setInvites(db.costingInvites || []);
    setQuotations(db.supplierQuotations || []);
    setRfqItems(db.rfqItems || []);
    setSavedFobs(db.savedFobs || []);
  }, [db]);

  // ── Dashboard Stats ──
  const dashStats = useMemo(() => {
    const byType = {}, byProdCat = {};
    const smvBuckets = { '< 10 SMV': 0, '10–20 SMV': 0, '20–35 SMV': 0, '> 35 SMV': 0 };
    const monthlyCap = Array(12).fill(0);

    suppliers.forEach(s => {
      byType[s.supplierType] = (byType[s.supplierType] || 0) + 1;

      // ── Per-category capacity from line-wise data ──────────────────────────
      // Formula per line: machines × 480 min × efficiency% ÷ SMV = pcs/day
      // Group sewingLines by their product category tag
      const linesByProdCat = {};
      (s.sewingLines || []).forEach(l => {
        const cat = (l.category || '').trim();
        if (!cat) return;
        if (!linesByProdCat[cat]) linesByProdCat[cat] = [];
        linesByProdCat[cat].push(l);
      });

      (s.productCats || []).forEach(pc => {
        if (!pc.category) return;
        if (!byProdCat[pc.category]) byProdCat[pc.category] = { count: 0, cap: 0, names: [] };
        byProdCat[pc.category].count++;
        if (!byProdCat[pc.category].names.includes(s.name)) byProdCat[pc.category].names.push(s.name);

        const smv = parseFloat(pc.smv || 0) || 20; // default SMV=20 if not entered
        const catLines = linesByProdCat[pc.category] || [];

        // If lines are tagged with this product category use them,
        // otherwise fall back to proportional share of total capacity
        let catCap = 0;
        if (catLines.length > 0) {
          catCap = catLines.reduce((acc, l) =>
            acc + (parseFloat(l.machines) || 0) * 480 * ((parseFloat(l.eff) || 0) / 100) / smv
          , 0);
        } else if (s.catCapacities && s.catCapacities[pc.category]) {
          // Use pre-computed value stored on save
          catCap = parseFloat(s.catCapacities[pc.category]) || 0;
        } else if ((s.productCats || []).length > 0) {
          // Fallback: divide total capacity equally across product categories
          catCap = parseFloat(s.totalCapacity || 0) / s.productCats.length;
        }

        byProdCat[pc.category].cap += catCap;

        // SMV bucket contribution
        if (smv > 0 && catCap > 0) {
          if (smv < 10)      smvBuckets['< 10 SMV']   += catCap;
          else if (smv < 20) smvBuckets['10–20 SMV']  += catCap;
          else if (smv < 35) smvBuckets['20–35 SMV']  += catCap;
          else               smvBuckets['> 35 SMV']   += catCap;
        }
      });

      // Monthly capacity: use per-category lines × working days per month
      // Then subtract already-allocated PO quantities for that month
      (s.calendar || []).forEach((m, i) => {
        const workingDays = parseFloat(m.workingDays || 26);
        monthlyCap[i] += parseFloat(s.totalCapacity || 0) * workingDays;
      });
    });

    // Subtract allocated PO quantities from monthly capacity
    // pos array comes from component scope via closure - use db directly
    return { byType, byProdCat, smvBuckets, monthlyCap };
  }, [suppliers, pos]);

  const stats = useMemo(() => ({
    total:    suppliers.length,
    garment:  suppliers.filter(s => s.supplierType === 'Garment Supplier').length,
    rm:       suppliers.filter(s => s.supplierType === 'Raw Material Supplier').length,
    va:       suppliers.filter(s => s.supplierType === 'VA Process Supplier').length,
    pendingPO: pos.filter(p => ['Draft', 'Pending'].includes(p.status)).length,
    openRfq:  invites.filter(i => i.status === 'Sent').length,
    quotes:   quotations.length
  }), [suppliers, pos, invites, quotations]);

  // ── BOM Fetcher (from StyleRegistry) ──
  const fetchBomForStyle = (styleRef) => {
    const style = (db.mgmtStyles || []).find(s => s.styleRef === styleRef);
    if (!style) return [];
    const raw = style.bom || style.bomItems || style.bomData || style.materials || [];
    return Array.isArray(raw) ? raw.map((item, i) => ({
      id:          item.id || i + 1,
      type:        item.type || item.category || item.materialType || 'Fabric',
      item:        item.name || item.item || item.description || `Item ${i + 1}`,
      qty:         item.qty || item.quantity || '-',
      unit:        item.unit || 'Mtr',
      composition: item.composition || item.content || '',
      description: item.description || item.spec || '',
      width:       item.width || '',
      gsm:         item.gsm || item.weight || '',
      color:       item.color || ''
    })) : [];
  };

  const getMatchedSuppliers = (bomItem) => {
    const t = (bomItem.type || '').toLowerCase();
    return suppliers.filter(s => {
      if (t.includes('fabric') || t.includes('yarn') || t.includes('lining')) return s.supplierType === 'Raw Material Supplier';
      if (t.includes('trim') || t.includes('button') || t.includes('zipper') || t.includes('label') || t.includes('tag') || t.includes('thread')) return s.supplierType === 'Raw Material Supplier';
      if (t.includes('embroider') || t.includes('print') || t.includes('wash') || t.includes('va') || t.includes('process')) return s.supplierType === 'VA Process Supplier';
      return s.supplierType === 'Garment Supplier';
    });
  };

  // ── Planning: line-wise capacity from ProductionPlanning ──
  const getPlanningLines = (styleRef) => {
    const plans = (db.planningAllocations || db.productionLogs || []).filter(p => p && p.styleRef === styleRef);
    return plans.flatMap(p => (p.lines || p.sewingLines || []));
  };

  // ── Handlers ──
  const handleSaveSupplier = async () => {
    if (!supForm.name) return alert('Company Name Required');
    let computed = { ...supForm };
    if (supForm.supplierType === 'Garment Supplier') {
      const totalMach    = supForm.sewingLines.reduce((a, b) => a + (parseFloat(b.machines) || 0), 0);
      const totalHelpers = supForm.sewingLines.reduce((a, b) => a + (parseFloat(b.helpers)  || 0), 0);
      const avgEff       = supForm.sewingLines.length > 0 ? supForm.sewingLines.reduce((a, b) => a + (parseFloat(b.eff) || 0), 0) / supForm.sewingLines.length : 0;

      // Total capacity using per-line SMV from productCats (falls back to SMV=20 if not set)
      const smvByCategory = {};
      (supForm.productCats || []).forEach(pc => {
        if (pc.category) smvByCategory[pc.category] = parseFloat(pc.smv || 0) || 20;
      });
      computed.totalCapacity = supForm.sewingLines.reduce((acc, l) => {
        const smv = smvByCategory[(l.category||'').trim()] || 20;
        return acc + ((parseFloat(l.machines) || 0) * 480 * ((parseFloat(l.eff) || 0) / 100) / smv);
      }, 0);

      // Per-category capacities stored for fast dashboard reads
      const catCapacities = {};
      supForm.sewingLines.forEach(l => {
        const cat = (l.category || '').trim();
        if (!cat) return;
        const smv = smvByCategory[cat] || 20;
        catCapacities[cat] = (catCapacities[cat] || 0) + ((parseFloat(l.machines)||0) * 480 * ((parseFloat(l.eff)||0)/100) / smv);
      });
      computed.catCapacities       = catCapacities;
      computed.factoryEff          = avgEff;
      computed.machineTotal        = totalMach;
      computed.operatorHelperRatio = totalHelpers > 0 ? (totalMach / totalHelpers).toFixed(2) : 0;
    }
    const payload  = { ...computed, type: 'SUPPLIER' };
    const newEntry = { ...payload, id: supForm.id || Date.now() };
    const newList  = supForm.id ? suppliers.map(s => s.id === supForm.id ? newEntry : s) : [...suppliers, newEntry];
    setSuppliers(newList);
    setDb(prev => ({ ...prev, suppliers: newList }));
    if (saveToDB) await saveToDB('common', payload);
    setSupForm({ ...INIT_SUP });
    alert('Supplier Profile Saved ✓');
  };

  const handleSaveProduct = async () => {
    if (!prodForm.name) return alert('Product Name Required');
    const calcRate = ((parseFloat(prodForm.fabPrice) || 0) + (parseFloat(prodForm.cm) || 0) + (parseFloat(prodForm.ohp) || 0)).toFixed(2);
    const payload  = { ...prodForm, rate: parseFloat(calcRate) > 0 ? calcRate : prodForm.rate, type: 'SOURCING_PRODUCT', id: prodForm.id || Date.now() };
    const newList  = [...products, payload];
    setProducts(newList);
    setDb(prev => ({ ...prev, sourcingProducts: newList }));
    if (saveToDB) await saveToDB('common', payload);
    setProdForm({ ...INIT_PROD });
    alert('Product Saved ✓');
  };

  const handleSavePO = async () => {
    if (!poForm.poNo) return alert('PO Number Required');
    const payload = { ...poForm, type: 'ALLOCATION', id: Date.now() };
    const newList = [...pos, payload];
    setPos(newList);
    setDb(prev => ({ ...prev, allocations: newList }));
    if (saveToDB) await saveToDB('production', payload);
    setPoForm({ ...INIT_PO });
  };

  const handleInviteLog = async (supName, item) => {
    const payload = { supplier: supName, items: item, date: new Date().toISOString().split('T')[0], status: 'Sent', type: 'INVITE_LOG', id: Date.now() };
    const newList = [...invites, payload];
    setInvites(newList);
    setDb(prev => ({ ...prev, costingInvites: newList }));
    if (saveToDB) await saveToDB('common', payload);
  };

  const dispatchRFQ = async (bomItem, sup, channel) => {
    const styleObj = (db.mgmtStyles || []).find(s => s.styleRef === selectedStyle) || {};
    const details  = [
      `*RFQ — Quotation Invitation*`,
      `Style: ${selectedStyle}`,
      `Item: ${bomItem.item}`,
      `Type: ${bomItem.type}`,
      `Qty: ${bomItem.qty} ${bomItem.unit || ''}`,
      bomItem.composition ? `Composition: ${bomItem.composition}` : '',
      bomItem.description ? `Spec: ${bomItem.description}` : '',
      bomItem.width ? `Width: ${bomItem.width}` : '',
      bomItem.gsm ? `GSM/Weight: ${bomItem.gsm}` : '',
      `\nPlease share: Price, Lead Time, MOQ & Payment Terms.`,
      `Return Address: ${sup.contact?.email || 'procurement@yourcompany.com'}`
    ].filter(Boolean).join('\n');

    if (channel === 'whatsapp') {
      const ph  = (sup.contact?.whatsapp || sup.contact?.mobile || '').replace(/\D/g, '');
      window.open(`https://wa.me/${ph}?text=${encodeURIComponent(details)}`, '_blank');
    } else {
      window.open(`mailto:${sup.contact?.email || ''}?subject=${encodeURIComponent(`RFQ: ${bomItem.item} – ${selectedStyle}`)}&body=${encodeURIComponent(details)}`, '_blank');
    }

    await handleInviteLog(sup.name, bomItem.item);
    const entry   = { id: Date.now(), bomItem, supplier: sup.name, channel, styleRef: selectedStyle, date: new Date().toISOString().split('T')[0], status: 'Sent' };
    const newList = [...rfqItems, entry];
    setRfqItems(newList);
    setDb(prev => ({ ...prev, rfqItems: newList }));
    alert(`RFQ sent to ${sup.name} via ${channel === 'whatsapp' ? 'WhatsApp' : 'Email'} ✓`);
  };

  const handleSaveQuote = async () => {
    if (!quoteInput.supplier || !quoteInput.item || !quoteInput.price) return alert('Supplier, Item & Price required');
    const payload  = { ...quoteInput, type: 'SUPPLIER_QUOTE', id: Date.now(), date: new Date().toISOString().split('T')[0], styleRef: selectedStyle, status: 'Received' };
    const newList  = [...quotations, payload];
    setQuotations(newList);
    setDb(prev => ({ ...prev, supplierQuotations: newList }));
    if (saveToDB) await saveToDB('common', payload);
    setQuoteInput({ supplier: '', item: '', price: '', unit: '', moq: '', leadTime: '', notes: '' });
    alert('Quotation recorded ✓');
  };

  const handleSaveFOB = async () => {
    const items   = Object.entries(fobSelections).map(([k, q]) => ({ item: k, supplier: q.supplier, price: q.price, unit: q.unit, leadTime: q.leadTime }));
    const calcFob = items.reduce((a, b) => a + parseFloat(b.price || 0), 0).toFixed(2);
    const total   = manualFob || calcFob;
    const entry   = { id: Date.now(), styleRef: selectedStyle, items, totalFob: total, comment: fobComment, date: new Date().toISOString().split('T')[0], status: 'Draft', type: 'FOB_COSTING' };
    const newList = [...savedFobs, entry];
    setSavedFobs(newList);
    setDb(prev => ({ ...prev, savedFobs: newList }));
    if (saveToDB) await saveToDB('common', entry);
    setFobSelections({}); setFobComment(''); setManualFob('');
    alert('FOB Costing Saved ✓');
  };

  const handleApproveFOB = async (fob) => {
    const updated = savedFobs.map(f => f.id === fob.id ? { ...f, status: 'Approved' } : f);
    setSavedFobs(updated);
    setDb(prev => ({ ...prev, savedFobs: updated }));
    // Push to CostingSummary-compatible structure in db.ws
    const ws = db.ws || {};
    const approvedCostings = [...(ws.approvedFobs || []), fob];
    setDb(prev => ({ ...prev, ws: { ...(prev.ws || {}), approvedFobs: approvedCostings } }));
    alert('FOB Approved! Notify suppliers from Quotations tab.');
  };

  const deleteItem = (list, setList, id, dbKey) => {
    const n = list.filter(i => i.id !== id);
    setList(n);
    setDb(prev => ({ ...prev, [dbKey]: n }));
  };

  // Shared line code formula — used by ALL modules for consistent identification
  const makeLineCode = (factoryId, lineIndex) => {
    const fid = String(factoryId || supForm.id || Date.now()).slice(-3).padStart(3,'0');
    const lid = String(lineIndex + 1).padStart(2,'0');
    return `LINE${fid}-${lid}`;  // e.g. LINE001-01
  };

  const generateLines = (count) => {
    const factId = supForm.id || Date.now();
    setSupForm(prev => ({
      ...prev,
      sewingLines: Array(parseInt(count) || 0).fill(null).map((_, i) => ({
        id: i + 1,
        line_code: makeLineCode(factId, i),   // ← unique ID assigned at creation
        category: '', machines: 0, iron: 0, qc: 0, helpers: 0, eff: 0,
      }))
    }));
  };

  const applyLineToAll = (idx) => {
    const src = supForm.sewingLines[idx];
    setSupForm(prev => ({ ...prev, sewingLines: prev.sewingLines.map(l => ({ ...l, ...src, id: l.id })) }));
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDERER: COMMON REGISTRATION FIELDS  (Strictly Preserved + Enhanced)
  // ─────────────────────────────────────────────────────────────────
  const renderCommon = () => (
    <>
      <Section title="1. Identity & Location">
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '15px' }}>
          <Field label="Company Name" value={supForm.name} onChange={e => setSupForm({ ...supForm, name: e.target.value })} />
          <Field label="Company ID (CIN)" value={supForm.companyId} onChange={e => setSupForm({ ...supForm, companyId: e.target.value })} />
          <Field label="Tax ID (GST/VAT)" value={supForm.taxId} onChange={e => setSupForm({ ...supForm, taxId: e.target.value })} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginTop: '12px' }}>
          <Field label="Head Office – City, State" value={supForm.address.city} onChange={e => setSupForm({ ...supForm, address: { ...supForm.address, city: e.target.value } })} placeholder="City, State" />
          <Field label="Country & ZIP" value={supForm.address.country} onChange={e => setSupForm({ ...supForm, address: { ...supForm.address, country: e.target.value } })} placeholder="Country – ZIP" />
          <Field label="Head Office Lat, Lng" value={supForm.address.lat} onChange={e => setSupForm({ ...supForm, address: { ...supForm.address, lat: e.target.value } })} placeholder="28.6139, 77.2090" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginTop: '12px' }}>
          <Field label="Factory – City, State" value={supForm.factoryAddress.city} onChange={e => setSupForm({ ...supForm, factoryAddress: { ...supForm.factoryAddress, city: e.target.value } })} placeholder="City, State" />
          <Field label="Factory Country & ZIP" value={supForm.factoryAddress.country} onChange={e => setSupForm({ ...supForm, factoryAddress: { ...supForm.factoryAddress, country: e.target.value } })} placeholder="Country – ZIP" />
          <Field label="Factory Lat, Lng" value={supForm.factoryAddress.lat} onChange={e => setSupForm({ ...supForm, factoryAddress: { ...supForm.factoryAddress, lat: e.target.value } })} placeholder="26.9124, 75.7873" />
        </div>
        <div style={{ marginTop: '12px' }}>
          <Field label="Ownership Type" options={['Sole Proprietorship', 'Partnership', 'Company (Board)', 'Other']} value={supForm.ownership} onChange={e => setSupForm({ ...supForm, ownership: e.target.value })} />
        </div>
      </Section>

      <Section title="2. Contact Information">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          <Field label="Phone" value={supForm.contact.phone} onChange={e => setSupForm({ ...supForm, contact: { ...supForm.contact, phone: e.target.value } })} placeholder="+91-XXXXXXXXXX" />
          <Field label="Mobile / WhatsApp" value={supForm.contact.mobile} onChange={e => setSupForm({ ...supForm, contact: { ...supForm.contact, mobile: e.target.value, whatsapp: e.target.value } })} placeholder="+91-XXXXXXXXXX" />
          <Field label="Email" value={supForm.contact.email} onChange={e => setSupForm({ ...supForm, contact: { ...supForm.contact, email: e.target.value } })} placeholder="info@company.com" />
          <Field label="Website" value={supForm.contact.website} onChange={e => setSupForm({ ...supForm, contact: { ...supForm.contact, website: e.target.value } })} placeholder="www.company.com" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '10px' }}>
          <Field label="LinkedIn" value={supForm.contact.linkedin} onChange={e => setSupForm({ ...supForm, contact: { ...supForm.contact, linkedin: e.target.value } })} placeholder="linkedin.com/company/..." />
          <Field label="Instagram" value={supForm.contact.instagram} onChange={e => setSupForm({ ...supForm, contact: { ...supForm.contact, instagram: e.target.value } })} placeholder="@handle" />
          <Field label="WhatsApp (if different)" value={supForm.contact.whatsapp} onChange={e => setSupForm({ ...supForm, contact: { ...supForm.contact, whatsapp: e.target.value } })} placeholder="+91-XXXXXXXXXX" />
        </div>
      </Section>

      <Section title="3. Management & Clients">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>Owners / Directors</label>
            <DynamicTable columns={[{ label: 'Name', key: 'name' }, { label: 'Designation', key: 'des' }]} data={supForm.owners} setData={d => setSupForm({ ...supForm, owners: d })} onAdd={() => setSupForm({ ...supForm, owners: [...supForm.owners, { name: '', des: '' }] })} />
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>Factories / Production Units</label>
            <DynamicTable columns={[{ label: 'Factory Name', key: 'name' }, { label: 'Address', key: 'addr' }]} data={supForm.factories} setData={d => setSupForm({ ...supForm, factories: d })} onAdd={() => setSupForm({ ...supForm, factories: [...supForm.factories, { name: '', addr: '' }] })} />
          </div>
        </div>
        <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginTop: '10px', display: 'block' }}>Customers & Markets</label>
        <DynamicTable columns={[{ label: 'Customer Name', key: 'name' }, { label: 'Share %', key: 'share' }, { label: 'Market', key: 'market' }]} data={supForm.customers} setData={d => setSupForm({ ...supForm, customers: d })} onAdd={() => setSupForm({ ...supForm, customers: [...supForm.customers, { name: '', share: '', market: '' }] })} />
      </Section>

      <Section title="4. Company & Factory Media">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>Company Logo (URL)</label>
            <input value={supForm.images.companyLogo} onChange={e => setSupForm({ ...supForm, images: { ...supForm.images, companyLogo: e.target.value } })} placeholder="https://..." style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', marginTop: '5px', boxSizing: 'border-box' }} />
            {supForm.images.companyLogo && <img src={supForm.images.companyLogo} alt="logo" style={{ height: '48px', marginTop: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }} onError={e => (e.target.style.display = 'none')} />}
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>Company Images (comma-separated URLs)</label>
            <input value={(supForm.images.companyImages || []).join(', ')} onChange={e => setSupForm({ ...supForm, images: { ...supForm.images, companyImages: e.target.value.split(',').map(u => u.trim()).filter(Boolean) } })} placeholder="https://img1.jpg, https://img2.jpg" style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', marginTop: '5px', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>Factory Images (comma-separated URLs)</label>
            <input value={(supForm.images.factoryImages || []).join(', ')} onChange={e => setSupForm({ ...supForm, images: { ...supForm.images, factoryImages: e.target.value.split(',').map(u => u.trim()).filter(Boolean) } })} placeholder="https://factory1.jpg, https://factory2.jpg" style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', marginTop: '5px', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>Company Profile PDF (URL)</label>
            <input value={supForm.files.profile} onChange={e => setSupForm({ ...supForm, files: { ...supForm.files, profile: e.target.value } })} placeholder="https://profile.pdf" style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', marginTop: '5px', boxSizing: 'border-box' }} />
          </div>
        </div>
      </Section>

      {/* ── Agreed Wastage Section ── */}
      <Section title="5. Agreed Wastage Terms">
        <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:'20px', alignItems:'start' }}>
          <div>
            <label style={{ fontSize:'11px', fontWeight:'bold', color:'#64748b' }}>Agreed Wastage % with this Supplier</label>
            <input
              type="number"
              value={supForm.agreedWastage}
              onChange={e => setSupForm({ ...supForm, agreedWastage: e.target.value })}
              placeholder="e.g. 5"
              style={{ width:'100%', padding:'8px', border:'2px solid #f59e0b', borderRadius:'4px', fontSize:'14px', marginTop:'5px', boxSizing:'border-box', fontWeight:'bold', color:'#92400e', background:'#fffbeb' }}
            />
          </div>
          <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:'8px', padding:'14px', fontSize:'12px', color:'#92400e' }}>
            <div style={{ fontWeight:'bold', marginBottom:'6px' }}>📦 How this is used:</div>
            <ul style={{ margin:0, paddingLeft:'18px', lineHeight:'1.8' }}>
              <li>This is the <strong>supplier-agreed</strong> wastage — the extra % they supply against each order.</li>
              <li>When raising a Purchase Order, the system compares this against the <strong>permissible wastage</strong> set in Global Masters for the fabric/trim.</li>
              <li>If there is a difference (e.g. agreed = 7%, permissible = 5%), the system will <strong>alert you to order less by 2%</strong> to avoid over-purchasing.</li>
            </ul>
          </div>
        </div>
      </Section>
    </>
  );

  // ── Calendar Renderer ──────────────────────────────────────────────────────
  const renderCalendar = () => {
    // Actual days per month (non-leap year; close enough for capacity planning)
    const MONTH_DAYS = [31,28,31,30,31,30,31,31,30,31,30,31];
    // Typical Sundays per month
    const SUNDAYS    = [4, 4, 4, 5, 4, 4, 4, 4, 5, 4, 4, 4];

    return (
      <Section title="Capacity Calendar & Holidays">
        <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'10px', background:'#fffbeb', padding:'8px 12px', borderRadius:'6px', border:'1px solid #fde68a' }}>
          <b>How to fill:</b> Enter only <b>Public Holidays</b> (factory closed days like Diwali, Eid etc.) for each month.
          Working days are auto-calculated as: <b>Total days − Sundays − Public Holidays</b>.
          Leave days are not entered here — they affect efficiency but not capacity planning.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
          {supForm.calendar.map((month, i) => {
            const totalDays   = MONTH_DAYS[i];
            const sundays     = SUNDAYS[i];
            const holidays    = parseInt(month.holidays) || 0;
            const netWorkDays = Math.max(0, totalDays - sundays - holidays);
            return (
              <div key={i} style={{ border: '1px solid #e2e8f0', padding: '10px', borderRadius: '6px', background: '#f8fafc' }}>
                <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '8px', color: '#334155', display:'flex', justifyContent:'space-between' }}>
                  {MONTHS_SHORT[i]}
                  <span style={{ fontSize:'10px', color:'#94a3b8', fontWeight:'400' }}>{totalDays}d</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div>
                    <label style={{ fontSize:'9px', color:'#64748b', fontWeight:'600', textTransform:'uppercase', display:'block', marginBottom:'2px' }}>Public Holidays</label>
                    <input type="number" min="0" max="15"
                      value={month.holidays}
                      onChange={e => {
                        const n = [...supForm.calendar];
                        n[i] = { ...n[i], holidays: parseInt(e.target.value) || 0 };
                        n[i].workingDays = Math.max(0, MONTH_DAYS[i] - SUNDAYS[i] - (parseInt(e.target.value)||0));
                        setSupForm({ ...supForm, calendar: n });
                      }}
                      style={{ width:'100%', fontSize: '12px', padding: '5px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', boxSizing:'border-box', fontWeight:'700' }}
                    />
                  </div>
                  <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700', marginTop:'2px', padding:'4px 0' }}>
                    ✓ {netWorkDays} working days
                  </div>
                  <div style={{ fontSize: '9px', color: '#94a3b8', lineHeight:1.4 }}>
                    {totalDays}d − {sundays} Sun − {holidays} hol
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    );
  };

  // ── Evaluation Section ──
  const renderEvaluation = () => {
    const ev = supForm.evaluation;
    const SUST_CERTS = ['GOTS', 'OEKO-TEX', 'GRS', 'BCI', 'Fairtrade', 'SA8000', 'WRAP', 'BSCI', 'LEED'];
    const QUAL_CERTS = ['ISO 9001', 'ISO 14001', 'SEDEX', 'SGS Approved', 'Intertek Approved', 'Bureau Veritas'];
    const toggle = (key, val) => {
      const arr = ev[key] || [];
      setSupForm({ ...supForm, evaluation: { ...ev, [key]: arr.includes(val) ? arr.filter(c => c !== val) : [...arr, val] } });
    };
    return (
      <Section title="Evaluation Criteria">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div style={{ background: '#f0fdf4', padding: '15px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Leaf size={16} color="#16a34a" />
              <span style={{ fontWeight: 'bold', color: '#15803d', fontSize: '13px' }}>SUSTAINABILITY</span>
            </div>
            <label style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>Overall Score</label>
            <div style={{ margin: '5px 0 12px' }}><RatingStars value={ev.sustScore || 0} onChange={v => setSupForm({ ...supForm, evaluation: { ...ev, sustScore: v } })} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              <Field label="Water Usage (L/unit)" value={ev.waterUsage} onChange={e => setSupForm({ ...supForm, evaluation: { ...ev, waterUsage: e.target.value } })} />
              <Field label="GHG Emission (kg CO₂/unit)" value={ev.ghgEmission} onChange={e => setSupForm({ ...supForm, evaluation: { ...ev, ghgEmission: e.target.value } })} />
            </div>
            <Field label="Waste Management" options={['Basic', 'Moderate', 'Advanced', 'Zero Waste']} value={ev.wasteMgmt} onChange={e => setSupForm({ ...supForm, evaluation: { ...ev, wasteMgmt: e.target.value } })} style={{ marginBottom: '10px' }} />
            <Field label="Social Audit Score (%)" value={ev.socialAudit} onChange={e => setSupForm({ ...supForm, evaluation: { ...ev, socialAudit: e.target.value } })} />
            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>Certifications</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                {SUST_CERTS.map(c => (
                  <span key={c} onClick={() => toggle('certifications', c)} style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '10px', cursor: 'pointer', fontWeight: '600', background: (ev.certifications || []).includes(c) ? '#16a34a' : '#e2e8f0', color: (ev.certifications || []).includes(c) ? 'white' : '#64748b' }}>{c}</span>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: '#eff6ff', padding: '15px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Shield size={16} color="#2563eb" />
              <span style={{ fontWeight: 'bold', color: '#1d4ed8', fontSize: '13px' }}>QUALITY</span>
            </div>
            <label style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>Quality Score</label>
            <div style={{ margin: '5px 0 12px' }}><RatingStars value={ev.qualityScore || 0} onChange={v => setSupForm({ ...supForm, evaluation: { ...ev, qualityScore: v } })} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              <Field label="Rejection Rate (%)" value={ev.rejectionRate} onChange={e => setSupForm({ ...supForm, evaluation: { ...ev, rejectionRate: e.target.value } })} />
              <Field label="Last Audit Score (%)" value={ev.lastAuditScore} onChange={e => setSupForm({ ...supForm, evaluation: { ...ev, lastAuditScore: e.target.value } })} />
            </div>
            <Field label="Last Audit Date" type="date" value={ev.auditDate} onChange={e => setSupForm({ ...supForm, evaluation: { ...ev, auditDate: e.target.value } })} />
            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>Quality Certifications</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                {QUAL_CERTS.map(c => (
                  <span key={c} onClick={() => toggle('qualityCerts', c)} style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '10px', cursor: 'pointer', fontWeight: '600', background: (ev.qualityCerts || []).includes(c) ? '#2563eb' : '#e2e8f0', color: (ev.qualityCerts || []).includes(c) ? 'white' : '#64748b' }}>{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>
    );
  };

  // ─────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>

      {/* ── NAV ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e293b,#334155)', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '52px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '30px', height: '30px', background: '#2563eb', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Layers size={16} color="white" />
          </div>
          <span style={{ fontSize: '15px', fontWeight: '800', color: 'white', letterSpacing: '0.03em' }}>SOURCING PORTAL</span>
        </div>
        <div style={{ display: 'flex', gap: '2px', overflowX: 'auto' }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '14px 12px', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '10px', background: 'transparent', color: activeTab === tab ? 'white' : '#94a3b8', borderBottom: activeTab === tab ? '3px solid #60a5fa' : '3px solid transparent', whiteSpace: 'nowrap', transition: 'all 0.15s', letterSpacing: '0.04em' }}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px', maxWidth: '1500px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>


        {activeTab==='AUDIT' && (()=>{

          const AUDIT_SECTIONS = [
            { key:'lab', title:'Section 1: On-Site Laboratory',
              questions:[
                { id:'l1', text:'Is there an on-site, fully equipped laboratory with trained technician?', critical:false },
                { id:'l2', text:'Is your laboratory certified by major international testing bodies?', critical:false },
                { id:'l3', text:'Are critical laboratory tests performed (in-house or 3rd party) and results used for prevention?', critical:false },
                { id:'l4', text:'Are consistent fabric/garment shrinkage/wash tests conducted and properly evaluated?', critical:false },
              ]
            },
            { key:'sample', title:'Section 2: Sample Room, Pattern & Marker Making',
              questions:[
                { id:'s1', text:'Is the sample room fully equipped to produce representative samples?', critical:false },
                { id:'s2', text:'Is there a skilled sample room technician capable of pattern modification with handover records? [Critical]', critical:true },
                { id:'s3', text:'Are CAD system and plotter used for grading, consumption and marker making?', critical:false },
                { id:'s4', text:'Are bulk patterns and markers checked for accuracy?', critical:false },
                { id:'s5', text:'Are shrinkage tests conducted for each bulk lot received?', critical:false },
                { id:'s6', text:'Are sample room fabric, trims & accessories well organized? Is housekeeping acceptable?', critical:false },
                { id:'s7', text:'Is lighting adequate (100 FC and above)?', critical:false },
              ]
            },
            { key:'warehouse', title:'Section 3: Warehousing of Fabrics, Yarns, Trims & Accessories',
              questions:[
                { id:'w1', text:'Are all fabrics/yarns/trims stored indoors, off floors, covered and properly organized? [Critical]', critical:true },
                { id:'w2', text:'Are inspection processes appropriate for bulk fabrics/yarns with trained inspectors?', critical:false },
                { id:'w3', text:'Are inspection processes appropriate for bulk trims/accessories with trained inspectors?', critical:false },
                { id:'w4', text:'Is light box and approved shade band available for bulk color verification?', critical:false },
                { id:'w5', text:'Is lighting adequate for all inspection areas (100 FC and above)?', critical:false },
              ]
            },
            { key:'cutting', title:'Section 4: Spreading, Cutting & Fusing',
              questions:[
                { id:'c1', text:'Is there adequate WIP balance between cutting and production output?', critical:false },
                { id:'c2', text:'Are acceptable processes in place for adequate fabric relaxation?', critical:false },
                { id:'c3', text:'Are shade variances controlled within the spreading process?', critical:false },
                { id:'c4', text:'Is spreading methodology acceptable?', critical:false },
                { id:'c5', text:'Is there proper control for matching/cutting of patterned fabrics?', critical:false },
                { id:'c6', text:'Does factory have adequate storage for bulk cut inventory?', critical:false },
                { id:'c7', text:'Are cut pieces properly inspected and numbered?', critical:false },
                { id:'c8', text:'Is fusing & heat seal quality acceptable with machines calibrated and records maintained?', critical:false },
                { id:'c9', text:'Are bulk cut goods transported in an organized and protected manner with records?', critical:false },
                { id:'c10', text:'Is lighting adequate (100 FC and above)?', critical:false },
                { id:'c11', text:'Does cutting have its own approved sample for reference?', critical:false },
              ]
            },
            { key:'production', title:'Section 5: Production Processes',
              questions:[
                { id:'p1', text:'Is the on-site industrial engineering department adequate and effective?', critical:false },
                { id:'p2', text:'Is there a production planning department?', critical:false },
                { id:'p3', text:'Is there a proper training program for new operators?', critical:false },
                { id:'p4', text:'Are pre-production meetings and pilot runs conducted for each style?', critical:false },
                { id:'p5', text:'Are approved samples available on every line with comments in local language?', critical:false },
                { id:'p6', text:'Are production lines well balanced and set up systematically without bottlenecks?', critical:false },
                { id:'p7', text:'Is there a proper system to record broken/damaged needles and sharp objects? [Critical]', critical:true },
                { id:'p8', text:'Is production floor well organized, clean and aisles free of clutter?', critical:false },
                { id:'p9', text:'Are adequate systems used to isolate defective and/or repaired products? [Critical]', critical:true },
                { id:'p10', text:'Are on-site mechanics regularly maintaining all machines with records kept?', critical:false },
                { id:'p11', text:'Is there a proper maintenance department with adequate tools?', critical:false },
                { id:'p12', text:'Are sharp objects (trimmers, scissors) effectively tracked and monitored? [Critical]', critical:true },
                { id:'p13', text:'Is lighting adequate (100 FC and above)?', critical:false },
              ]
            },
            { key:'inspection', title:'Section 6: Inspection Processes',
              questions:[
                { id:'i1', text:'Does factory have inline and end-line inspection with complete garment measurement? [Critical]', critical:true },
                { id:'i2', text:'Are returned garments from sub-contractors inspected before re-entering bulk production?', critical:false },
                { id:'i3', text:'Does QA/QC have independent reporting structure? Are all QA/QC trained regularly? [Critical]', critical:true },
                { id:'i4', text:'Does factory have a complete set of technical manuals and knows how to get updates?', critical:false },
                { id:'i5', text:'Does factory conduct daily inspection after sewing line using standard AQL?', critical:false },
                { id:'i6', text:'Is lighting adequate (100 FC and above)?', critical:false },
                { id:'i7', text:'How are alteration/defective garments handled in all sections?', critical:false },
                { id:'i8', text:'Does factory have sufficient, well-equipped inspection room?', critical:false },
              ]
            },
            { key:'pressing', title:'Section 7: Pressing',
              questions:[
                { id:'pr1', text:'Is there a pressing supervisor with standards displayed? Sufficient space for garments?', critical:false },
                { id:'pr2', text:'Does factory use appropriate pressing equipment with calibration and maintenance records?', critical:false },
                { id:'pr3', text:'Is there measurement audit after pressing with results properly documented?', critical:false },
                { id:'pr4', text:'Is ventilation and lighting (80 FC) adequate?', critical:false },
                { id:'pr5', text:'Are pressed garments handled properly prior to packing?', critical:false },
              ]
            },
            { key:'finishing', title:'Section 8: Finishing, Repairing & Packing',
              questions:[
                { id:'f1', text:'Are packaging standards and samples readily available for reference?', critical:false },
                { id:'f2', text:'Is there 100% final checking after finishing with random audit? [Critical]', critical:true },
                { id:'f3', text:'Is there an effective stain management and repair operation on site?', critical:false },
                { id:'f4', text:'Is there an organized and efficient work flow in the finishing area?', critical:false },
                { id:'f5', text:'Is lighting adequate (100 FC for inspection, 60-80 for other areas)?', critical:false },
                { id:'f6', text:'Does factory conduct final audit based on standard or tighter AQL? [Critical]', critical:true },
                { id:'f7', text:'How are alteration/defective garments handled in the section?', critical:false },
                { id:'f8', text:'How is the infrastructure and housekeeping in the section?', critical:false },
                { id:'f9', text:'Is the storage space for packed goods sufficient as per factory capacity?', critical:false },
                { id:'f10', text:'Is there a procedure to remove uncut thread, loose threads and foreign materials?', critical:false },
              ]
            },
            { key:'safety', title:'Section 9: Product Safety & Satisfaction',
              questions:[
                { id:'ps1', text:'Does factory have sufficient metal detection process for all products? (Mandatory) [Critical]', critical:true },
                { id:'ps2', text:'Does factory have pull strength equipment for buttons/snaps/rivets? [Critical]', critical:true },
                { id:'ps3', text:'Does factory use lockstitch for button attach? [Critical]', critical:true },
                { id:'ps4', text:'How is Snap/Rivet attaching process and machinery in the factory? [Critical]', critical:true },
                { id:'ps5', text:'Is there arrangement for disposal of non-conforming goods and materials? [Critical]', critical:true },
                { id:'ps6', text:'Does factory maintain chemical safety tests for printing items? [Critical]', critical:true },
              ]
            },
          ];

          const SCORE_COLORS = { '10':'#16a34a', '5':'#2563eb', '3':'#d97706', '0':'#dc2626', 'NA':'#64748b' };


          const audits = db.factoryAudits || [];

          const calcSection = (sec) => {
            let earned=0, possible=0, criticalFail=false;
            sec.questions.forEach(q=>{
              if(auditNA[q.id]) return;
              possible += 10;
              const v=parseFloat(auditScores[q.id]||0);
              earned += v;
              if(q.critical && v===0) criticalFail=true;
            });
            return { earned, possible, pct:possible>0?(earned/possible)*100:0, criticalFail };
          };

          const calcTotal = () => {
            let te=0, tp=0, anyFail=false;
            AUDIT_SECTIONS.forEach(s=>{ const r=calcSection(s); te+=r.earned; tp+=r.possible; if(r.criticalFail) anyFail=true; });
            const pct=tp>0?(te/tp)*100:0;
            const grade=anyFail?'C':pct>=80?'A':pct>=60?'B':'C';
            const status=anyFail?'Rejected (Critical Fail)':pct>=80?'Approved':pct>=60?'Provisionally Approved':'Rejected';
            return { te, tp, pct, grade, status, anyFail };
          };

          const scol = (s) => s&&s.includes('Approved')&&!s.includes('Prov')?'#16a34a':s&&s.includes('Prov')?'#d97706':'#dc2626';
          const sbg  = (s) => s&&s.includes('Approved')&&!s.includes('Prov')?'#f0fdf4':s&&s.includes('Prov')?'#fef3c7':'#fef2f2';

          const totals = calcTotal();

          const handleSaveAudit = async () => {
            if(!auditMeta.supplierName) return alert('Select a supplier');
            if(!auditMeta.auditorName)  return alert('Enter auditor name');
            const t=calcTotal();
            const entry={id:Date.now(),type:'FACTORY_AUDIT',...auditMeta,scores:{...auditScores},na:{...auditNA},sectionComments:{...sectionComments},totalScore:parseFloat(t.pct.toFixed(1)),earnedPoints:t.te,possiblePoints:t.tp,grade:t.grade,status:t.status,capRequired:t.grade!=='A',criticalFail:t.anyFail,date:auditMeta.auditDate};
            const newList=[...audits,entry];
            setDb(prev=>({...prev,factoryAudits:newList}));
            if(saveToDB) await saveToDB('common',entry);
            const supIdx=suppliers.findIndex(s=>s.name===auditMeta.supplierName);
            if(supIdx!==-1){
              const upd={...suppliers[supIdx],latestAuditScore:entry.totalScore,latestAuditGrade:entry.grade,latestAuditStatus:entry.status,latestAuditDate:entry.date};
              const ns=suppliers.map((s,i)=>i===supIdx?upd:s);
              setSuppliers(ns); setDb(prev=>({...prev,suppliers:ns}));
            }
            setAuditScores({}); setAuditNA({}); setSectionComments({});
            setAuditMeta({supplierName:'',auditorName:'',auditDate:new Date().toISOString().split('T')[0],auditType:'Full Audit',overallComments:''});
            setAuditView('list');
            alert('Audit saved! Score: '+t.pct.toFixed(1)+'% — '+t.status);
          };

          if(auditView==='list') {
            const byS={'Approved':0,'Provisionally Approved':0,'Rejected':0};
            audits.forEach(a=>{ if(byS[a.status]!==undefined) byS[a.status]++; else byS['Rejected']++; });
            return (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))', gap:'14px', marginBottom:'20px' }}>
                  {[
                    {label:'Total Audited',val:audits.length,color:'#2563eb',icon:'📋'},
                    {label:'Approved',val:byS['Approved'],color:'#16a34a',icon:'✅'},
                    {label:'Provisionally Approved',val:byS['Provisionally Approved'],color:'#d97706',icon:'⚠️'},
                    {label:'Rejected',val:byS['Rejected']||0,color:'#dc2626',icon:'❌'},
                    {label:'CAP Required',val:audits.filter(a=>a.capRequired).length,color:'#7c3aed',icon:'📝'},
                  ].map(card=>(
                    <div key={card.label} style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:'10px', padding:'14px', display:'flex', alignItems:'center', gap:'12px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
                      <div style={{ fontSize:'22px' }}>{card.icon}</div>
                      <div>
                        <div style={{ fontSize:'24px', fontWeight:'800', color:card.color, lineHeight:1 }}>{card.val}</div>
                        <div style={{ fontSize:'10px', color:'#64748b', marginTop:'3px' }}>{card.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
                  <h3 style={{ margin:0, color:'#1e293b', fontSize:'15px', fontWeight:'800' }}>Factory Audit Records</h3>
                  <button onClick={()=>setAuditView('new')} style={{ padding:'10px 20px', background:'#1e293b', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' }}>
                    + New Audit
                  </button>
                </div>
                {audits.length===0
                  ? <div style={{ textAlign:'center', padding:'50px', color:'#94a3b8', background:'white', borderRadius:'10px', border:'1px dashed #e2e8f0' }}>
                      <div style={{ fontSize:'36px', marginBottom:'10px' }}>📋</div>
                      <div style={{ fontWeight:'600' }}>No audits recorded yet</div>
                      <div style={{ fontSize:'12px', marginTop:'5px' }}>Click "+ New Audit" to begin a factory evaluation</div>
                    </div>
                  : <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px', background:'white', borderRadius:'8px', overflow:'hidden' }}>
                        <thead>
                          <tr style={{ background:'#1e293b', color:'white' }}>
                            {['Factory','Auditor','Date','Type','Score','Grade','Status','CAP','Actions'].map(h=>(
                              <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:'700', whiteSpace:'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...audits].sort((a,b)=>b.id-a.id).map((a,idx)=>(
                            <tr key={a.id} style={{ borderBottom:'1px solid #f1f5f9', background:idx%2===0?'white':'#fafafa' }}>
                              <td style={{ padding:'10px 12px', fontWeight:'700' }}>{a.supplierName}</td>
                              <td style={{ padding:'10px 12px', color:'#64748b' }}>{a.auditorName}</td>
                              <td style={{ padding:'10px 12px', color:'#64748b' }}>{a.auditDate}</td>
                              <td style={{ padding:'10px 12px', color:'#64748b', fontSize:'11px' }}>{a.auditType}</td>
                              <td style={{ padding:'10px 12px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                                  <div style={{ flex:1, height:'5px', background:'#f1f5f9', borderRadius:'3px', overflow:'hidden', minWidth:'50px' }}>
                                    <div style={{ height:'100%', width:a.totalScore+'%', background:a.totalScore>=80?'#16a34a':a.totalScore>=60?'#d97706':'#dc2626', borderRadius:'3px' }}/>
                                  </div>
                                  <span style={{ fontWeight:'bold', color:a.totalScore>=80?'#16a34a':a.totalScore>=60?'#d97706':'#dc2626' }}>{a.totalScore}%</span>
                                </div>
                              </td>
                              <td style={{ padding:'10px 12px', fontWeight:'800', fontSize:'14px', color:a.grade==='A'?'#16a34a':a.grade==='B'?'#d97706':'#dc2626' }}>{a.grade}</td>
                              <td style={{ padding:'10px 12px' }}>
                                <span style={{ padding:'3px 8px', background:sbg(a.status), color:scol(a.status), borderRadius:'12px', fontSize:'10px', fontWeight:'700', whiteSpace:'nowrap' }}>{a.status}</span>
                              </td>
                              <td style={{ padding:'10px 12px' }}>
                                {a.capRequired
                                  ? <span style={{ fontSize:'10px', color:'#7c3aed', background:'#ede9fe', padding:'2px 6px', borderRadius:'8px', fontWeight:'700' }}>Required</span>
                                  : <span style={{ fontSize:'10px', color:'#94a3b8' }}>—</span>}
                              </td>
                              <td style={{ padding:'10px 12px' }}>
                                <button onClick={()=>{ setViewAudit(a); setAuditView('view'); }} style={{ padding:'4px 10px', background:'#2563eb', color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>
            );
          }

          if(auditView==='view' && viewAudit) {
            return (
              <div>
                <div style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'20px' }}>
                  <button onClick={()=>setAuditView('list')} style={{ padding:'8px 14px', background:'#f1f5f9', color:'#334155', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'12px' }}>← Back</button>
                  <div style={{ fontWeight:'800', fontSize:'16px', color:'#1e293b' }}>{viewAudit.supplierName} — Audit Report</div>
                  <span style={{ padding:'4px 12px', background:sbg(viewAudit.status), color:scol(viewAudit.status), borderRadius:'20px', fontSize:'11px', fontWeight:'800' }}>{viewAudit.status}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'14px', marginBottom:'20px' }}>
                  {[{label:'Auditor',value:viewAudit.auditorName},{label:'Date',value:viewAudit.auditDate},{label:'Type',value:viewAudit.auditType},{label:'Score',value:viewAudit.totalScore+'% (Grade '+viewAudit.grade+')'}].map(f=>(
                    <div key={f.label} style={{ background:'white', padding:'14px', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
                      <div style={{ fontSize:'10px', color:'#94a3b8', fontWeight:'700', textTransform:'uppercase', marginBottom:'5px' }}>{f.label}</div>
                      <div style={{ fontWeight:'700', color:'#1e293b' }}>{f.value}</div>
                    </div>
                  ))}
                </div>
                {AUDIT_SECTIONS.map(sec=>{
                  const qs=sec.questions;
                  const earned=qs.filter(q=>!viewAudit.na?.[q.id]).reduce((a,q)=>a+(parseFloat(viewAudit.scores?.[q.id]||0)),0);
                  const possible=qs.filter(q=>!viewAudit.na?.[q.id]).length*10;
                  const pct=possible>0?(earned/possible)*100:0;
                  return (
                    <div key={sec.key} style={{ marginBottom:'12px', border:'1px solid #e2e8f0', borderRadius:'8px', overflow:'hidden', background:'white' }}>
                      <div style={{ background:'#f8fafc', padding:'10px 16px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontWeight:'700', color:'#334155', fontSize:'12px' }}>{sec.title}</span>
                        <span style={{ fontWeight:'800', color:pct>=80?'#16a34a':pct>=60?'#d97706':'#dc2626' }}>{pct.toFixed(0)}% ({earned}/{possible})</span>
                      </div>
                      <div style={{ padding:'10px 16px' }}>
                        {qs.map(q=>(
                          <div key={q.id} style={{ display:'flex', gap:'10px', alignItems:'flex-start', padding:'5px 0', borderBottom:'1px solid #f8fafc' }}>
                            <div style={{ flex:1, fontSize:'11px', color:'#334155', lineHeight:1.4 }}>
                              {q.critical && <span style={{ color:'#dc2626', marginRight:'4px' }}>●</span>}
                              {q.text}
                            </div>
                            <span style={{ fontSize:'13px', fontWeight:'800', minWidth:'32px', textAlign:'right', color:SCORE_COLORS[viewAudit.na?.[q.id]?'NA':(viewAudit.scores?.[q.id]||'0')]||'#94a3b8' }}>
                              {viewAudit.na?.[q.id]?'N/A':(viewAudit.scores?.[q.id]||'—')}
                            </span>
                          </div>
                        ))}
                        {viewAudit.sectionComments?.[sec.key] && (
                          <div style={{ marginTop:'8px', padding:'8px', background:'#fffbeb', borderRadius:'4px', fontSize:'11px', color:'#92400e', border:'1px solid #fde68a' }}>
                            <b>Comments:</b> {viewAudit.sectionComments[sec.key]}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }

          const sec       = AUDIT_SECTIONS.find(s=>s.key===activeSection);
          const secResult = calcSection(sec);
          const secIdx    = AUDIT_SECTIONS.findIndex(s=>s.key===activeSection);

          return (
            <div style={{ display:'grid', gridTemplateColumns:'210px 1fr', gap:'20px' }}>
              <div>
                <div style={{ background:'white', padding:'14px', borderRadius:'8px', border:'1px solid #e2e8f0', marginBottom:'12px' }}>
                  <div style={{ fontSize:'11px', fontWeight:'700', color:'#334155', marginBottom:'10px', textTransform:'uppercase' }}>Audit Details</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                    <div>
                      <label style={{ fontSize:'10px', fontWeight:'bold', color:'#64748b' }}>Factory *</label>
                      <select value={auditMeta.supplierName} onChange={e=>setAuditMeta({...auditMeta,supplierName:e.target.value})} style={{ width:'100%', padding:'6px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'11px', marginTop:'3px' }}>
                        <option value="">— Select —</option>
                        {suppliers.filter(s=>s.supplierType==='Garment Supplier').map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize:'10px', fontWeight:'bold', color:'#64748b' }}>Auditor Name *</label>
                      <input value={auditMeta.auditorName} onChange={e=>setAuditMeta({...auditMeta,auditorName:e.target.value})} placeholder="Full Name" style={{ width:'100%', padding:'6px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'11px', marginTop:'3px', boxSizing:'border-box' }}/>
                    </div>
                    <div>
                      <label style={{ fontSize:'10px', fontWeight:'bold', color:'#64748b' }}>Audit Date</label>
                      <input type="date" value={auditMeta.auditDate} onChange={e=>setAuditMeta({...auditMeta,auditDate:e.target.value})} style={{ width:'100%', padding:'6px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'11px', marginTop:'3px', boxSizing:'border-box' }}/>
                    </div>
                    <div>
                      <label style={{ fontSize:'10px', fontWeight:'bold', color:'#64748b' }}>Audit Type</label>
                      <select value={auditMeta.auditType} onChange={e=>setAuditMeta({...auditMeta,auditType:e.target.value})} style={{ width:'100%', padding:'6px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'11px', marginTop:'3px' }}>
                        {['Full Audit','Follow-up Audit','Pre-shipment Check','CAP Verification'].map(t=><option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
                <div style={{ background:'white', borderRadius:'8px', border:'1px solid #e2e8f0', overflow:'hidden', marginBottom:'12px' }}>
                  {AUDIT_SECTIONS.map(s=>{
                    const r=calcSection(s);
                    const answered=s.questions.filter(q=>auditScores[q.id]!==undefined||auditNA[q.id]).length;
                    return (
                      <div key={s.key} onClick={()=>setActiveSection(s.key)} style={{ padding:'8px 12px', cursor:'pointer', background:activeSection===s.key?'#1e293b':'white', borderBottom:'1px solid #f1f5f9' }}>
                        <div style={{ fontSize:'10px', fontWeight:'700', color:activeSection===s.key?'white':'#334155', lineHeight:1.3, marginBottom:'4px' }}>{s.title.split(':')[0]}</div>
                        <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                          <div style={{ flex:1, height:'3px', background:activeSection===s.key?'rgba(255,255,255,0.2)':'#f1f5f9', borderRadius:'2px', overflow:'hidden' }}>
                            <div style={{ height:'100%', width:r.pct+'%', background:r.pct>=80?'#16a34a':r.pct>=60?'#f59e0b':'#dc2626', borderRadius:'2px' }}/>
                          </div>
                          <span style={{ fontSize:'9px', color:activeSection===s.key?'#93c5fd':'#94a3b8' }}>{answered}/{s.questions.length}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ background:totals.grade==='A'?'#f0fdf4':totals.grade==='B'?'#fef3c7':'#fef2f2', padding:'12px', borderRadius:'8px', border:'2px solid '+scol(totals.status) }}>
                  <div style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', marginBottom:'6px' }}>Running Score</div>
                  <div style={{ fontSize:'28px', fontWeight:'900', color:scol(totals.status), lineHeight:1 }}>{totals.pct.toFixed(1)}%</div>
                  <div style={{ fontSize:'11px', color:'#64748b', margin:'4px 0' }}>{totals.te}/{totals.tp} pts</div>
                  <div style={{ padding:'3px 8px', background:scol(totals.status)+'15', color:scol(totals.status), borderRadius:'8px', fontSize:'10px', fontWeight:'800', display:'inline-block', marginBottom:'10px' }}>Grade {totals.grade}</div>
                  {totals.anyFail && <div style={{ fontSize:'10px', color:'#dc2626', background:'#fef2f2', padding:'5px 8px', borderRadius:'4px', marginBottom:'8px' }}>⚠ Critical question scored 0</div>}
                  <button onClick={handleSaveAudit} style={{ width:'100%', padding:'8px', background:'#16a34a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'11px', marginBottom:'6px' }}>💾 Save Audit</button>
                  <button onClick={()=>{ setAuditScores({}); setAuditNA({}); setSectionComments({}); setAuditView('list'); }} style={{ width:'100%', padding:'7px', background:'white', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'11px' }}>Cancel</button>
                </div>
              </div>
              <div>
                <div style={{ background:'white', borderRadius:'10px', border:'1px solid #e2e8f0', overflow:'hidden' }}>
                  <div style={{ background:'#1e293b', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:'800', color:'white', fontSize:'14px' }}>{sec.title}</div>
                      <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'2px' }}>10 = Excellent · 5 = Acceptable · 3 = Partial · 0 = Unacceptable · N/A</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:'22px', fontWeight:'900', color:secResult.pct>=80?'#4ade80':secResult.pct>=60?'#fbbf24':'#f87171' }}>{secResult.pct.toFixed(0)}%</div>
                      <div style={{ fontSize:'10px', color:'#94a3b8' }}>{secResult.earned}/{secResult.possible}</div>
                    </div>
                  </div>
                  {sec.questions.map((q,qi)=>{
                    const isNA=auditNA[q.id]||false;
                    const score=auditScores[q.id];
                    const scoreColor=SCORE_COLORS[score]||'#94a3b8';
                    return (
                      <div key={q.id} style={{ padding:'12px 20px', borderBottom:'1px solid #f8fafc', background:isNA?'#f8fafc':score==='0'&&q.critical?'#fff8f8':'white' }}>
                        <div style={{ display:'flex', gap:'12px', alignItems:'flex-start' }}>
                          <div style={{ width:'20px', height:'20px', borderRadius:'50%', background:'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:'800', color:'#64748b', flexShrink:0, marginTop:'2px' }}>{qi+1}</div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:'12px', color:'#1e293b', lineHeight:1.5, marginBottom:'8px' }}>
                              {q.critical && <span style={{ color:'#dc2626', marginRight:'4px' }}>●</span>}
                              {q.text}
                            </div>
                            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                              {[10,5,3,0].map(v=>(
                                <button key={v} onClick={()=>{ if(!isNA) setAuditScores(prev=>({...prev,[q.id]:String(v)})); }} disabled={isNA}
                                  style={{ padding:'5px 12px', borderRadius:'6px', border:'2px solid '+(score===String(v)?SCORE_COLORS[String(v)]:'#e2e8f0'), background:score===String(v)?SCORE_COLORS[String(v)]+'20':'white', color:score===String(v)?SCORE_COLORS[String(v)]:'#64748b', fontWeight:score===String(v)?'800':'500', cursor:isNA?'not-allowed':'pointer', fontSize:'12px', opacity:isNA?0.4:1 }}>
                                  {v}
                                </button>
                              ))}
                              <button onClick={()=>{ setAuditNA(prev=>({...prev,[q.id]:!isNA})); if(!isNA) setAuditScores(prev=>{ const n={...prev}; delete n[q.id]; return n; }); }}
                                style={{ padding:'5px 10px', borderRadius:'6px', border:'2px solid '+(isNA?'#64748b':'#e2e8f0'), background:isNA?'#64748b20':'white', color:isNA?'#64748b':'#94a3b8', fontWeight:isNA?'800':'500', cursor:'pointer', fontSize:'11px' }}>
                                N/A
                              </button>
                              {score!==undefined && !isNA && (
                                <button onClick={()=>setAuditScores(prev=>{ const n={...prev}; delete n[q.id]; return n; })} style={{ padding:'5px 8px', borderRadius:'6px', border:'1px solid #fecaca', background:'none', color:'#ef4444', cursor:'pointer', fontSize:'10px' }}>Clear</button>
                              )}
                            </div>
                          </div>
                          {score!==undefined && !isNA && (
                            <div style={{ width:'34px', height:'34px', borderRadius:'8px', background:scoreColor+'20', border:'2px solid '+scoreColor, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', fontWeight:'900', color:scoreColor, flexShrink:0 }}>{score}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ padding:'14px 20px', background:'#f8fafc', borderTop:'1px solid #e2e8f0' }}>
                    <label style={{ fontSize:'11px', fontWeight:'700', color:'#334155', display:'block', marginBottom:'6px' }}>
                      Section Comments / CAP Notes
                      {secResult.criticalFail && <span style={{ marginLeft:'8px', color:'#dc2626', fontSize:'10px' }}>⚠ CAP required</span>}
                    </label>
                    <textarea value={sectionComments[sec.key]||''} onChange={e=>setSectionComments(prev=>({...prev,[sec.key]:e.target.value}))}
                      placeholder="Enter observations, findings and corrective action required..."
                      style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px', height:'60px', resize:'vertical', boxSizing:'border-box' }}></textarea>
                  </div>
                  <div style={{ padding:'10px 20px', display:'flex', justifyContent:'space-between', borderTop:'1px solid #e2e8f0' }}>
                    <button onClick={()=>{ if(secIdx>0) setActiveSection(AUDIT_SECTIONS[secIdx-1].key); }} disabled={secIdx===0}
                      style={{ padding:'7px 14px', background:'#f1f5f9', color:'#334155', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'12px', opacity:secIdx===0?0.4:1 }}>← Previous</button>
                    <button onClick={()=>{ if(secIdx<AUDIT_SECTIONS.length-1) setActiveSection(AUDIT_SECTIONS[secIdx+1].key); }} disabled={secIdx===AUDIT_SECTIONS.length-1}
                      style={{ padding:'7px 14px', background:'#1e293b', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'12px', opacity:secIdx===AUDIT_SECTIONS.length-1?0.4:1 }}>Next →</button>
                  </div>
                </div>
                <div style={{ marginTop:'12px', padding:'12px', background:'white', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
                  <div style={{ fontWeight:'700', fontSize:'11px', color:'#334155', marginBottom:'8px', textTransform:'uppercase' }}>Rating Guide</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                    {[{r:'80–100% (A)',l:'Approved',c:'#16a34a',bg:'#f0fdf4'},{r:'60–79% (B)',l:'Provisionally Approved',c:'#d97706',bg:'#fef3c7'},{r:'0–59% (C)',l:'Rejected',c:'#dc2626',bg:'#fef2f2'}].map(g=>(
                      <div key={g.r} style={{ padding:'8px', background:g.bg, borderRadius:'6px', border:'1px solid '+g.c+'30' }}>
                        <div style={{ fontWeight:'800', color:g.c, fontSize:'11px' }}>{g.r}</div>
                        <div style={{ fontWeight:'700', color:g.c, fontSize:'10px', margin:'2px 0' }}>{g.l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop:'8px', fontSize:'10px', color:'#64748b' }}>● Critical questions scored 0 = automatic rejection regardless of overall score.</div>
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab==='DASHBOARD' && (()=>{
          const TYPE_COLORS={ 'Garment Supplier':'#2563eb', 'Raw Material Supplier':'#16a34a', 'VA Process Supplier':'#d97706' };
          const maxType  = Math.max(1,...Object.values(dashStats.byType));
          const maxSmv   = Math.max(1,...Object.values(dashStats.smvBuckets));
          const maxMonCap= Math.max(1,...dashStats.monthlyCap);
          const allCats  = Object.keys(dashStats.byProdCat);
          const shownCats= filterCat==='All' ? allCats : allCats.filter(c=>
            (dashStats.byProdCat[c].names||[]).some(n=>suppliers.find(s=>s.name===n)?.supplierType===filterCat)
          );

          return (
            <div>
              {/* ── KPI CARDS ── */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(165px,1fr))', gap:'14px', marginBottom:'22px' }}>
                <StatCard label="Factories Audited"    value={(db.factoryAudits||[]).length}                                                          color="#2563eb" icon={LayoutDashboard} onClick={()=>setActiveTab('AUDIT')}/>
                <StatCard label="Audit Approved"       value={(db.factoryAudits||[]).filter(a=>a.status==='Approved').length}                         color="#16a34a" icon={CheckSquare}     onClick={()=>setActiveTab('AUDIT')}/>
                <StatCard label="Under CAP"            value={(db.factoryAudits||[]).filter(a=>a.capRequired&&a.status!=='Rejected (Critical Fail)').length} color="#d97706" icon={AlertTriangle}  onClick={()=>setActiveTab('AUDIT')}/>
                <StatCard label="Total Suppliers"    value={stats.total}     color="#2563eb" icon={Users}       sub={`${stats.garment}G · ${stats.rm}RM · ${stats.va}VA`} onClick={()=>setActiveTab('REGISTRATION')}/>
                <StatCard label="Garment Suppliers"  value={stats.garment}   color="#2563eb" icon={Factory}     sub="Sewing units"/>
                <StatCard label="RM Suppliers"       value={stats.rm}        color="#16a34a" icon={Package}     sub="Fabric & Trims"/>
                <StatCard label="VA Process"         value={stats.va}        color="#d97706" icon={Zap}         sub="Print · Emb · Wash"/>
                <StatCard label="Open RFQs"          value={stats.openRfq}   color="#7c3aed" icon={Send}        sub="Awaiting quotes"/>
                <StatCard label="Quotes Received"    value={stats.quotes}    color="#0891b2" icon={FileText}    sub="Ready to review" onClick={()=>setActiveTab('QUOTATIONS')}/>
                <StatCard label="Active Allocations" value={stats.pendingPO} color="#ea580c" icon={ShoppingBag} sub="POs in progress"  onClick={()=>setActiveTab('ALLOCATION')}/>
              </div>

              {/* ── ROW 2: Type bars + SMV buckets ── */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
                <Section title="Supplier Category Distribution">
                  {Object.keys(dashStats.byType).length===0
                    ? <p style={{ color:'#94a3b8', fontSize:'13px', textAlign:'center', padding:'20px 0' }}>No suppliers yet</p>
                    : <>
                        {Object.entries(dashStats.byType).map(([type,cnt])=>(
                          <MiniBar key={type} label={type} value={cnt} max={maxType} color={TYPE_COLORS[type]||'#2563eb'} extra={`${((cnt/Math.max(1,stats.total))*100).toFixed(0)}%`}/>
                        ))}
                        <div style={{ marginTop:'12px', display:'flex', gap:'10px', flexWrap:'wrap' }}>
                          {Object.entries(dashStats.byType).map(([type,cnt])=>(
                            <div key={type} style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11px' }}>
                              <div style={{ width:'10px', height:'10px', borderRadius:'2px', background:TYPE_COLORS[type]||'#2563eb' }}/>
                              <span style={{ color:'#64748b' }}>{type.split(' ')[0]}: <b style={{ color:'#1e293b' }}>{cnt}</b></span>
                            </div>
                          ))}
                        </div>
                      </>
                  }
                </Section>

                <Section title="SMV Range × Capacity (pcs/day)">
                  {Object.values(dashStats.smvBuckets).every(v=>v===0)
                    ? <p style={{ color:'#94a3b8', fontSize:'13px', textAlign:'center', padding:'20px 0' }}>Add suppliers with product categories & SMV</p>
                    : Object.entries(dashStats.smvBuckets).map(([range,cap])=>(
                        <MiniBar key={range} label={`${range} SMV`} value={Math.round(cap)} max={maxSmv} color="#7c3aed" extra="pcs/day"/>
                      ))
                  }
                </Section>
              </div>

              {/* ── Product Category × Supplier Matrix ── */}
              <Section title="Product Category — Supplier Matrix"
                actions={
                  <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
                    style={{ fontSize:'11px', padding:'4px 8px', border:'1px solid #cbd5e1', borderRadius:'4px', background:'white' }}>
                    <option value="All">All Types</option>
                    <option value="Garment Supplier">Garment</option>
                    <option value="Raw Material Supplier">Raw Material</option>
                    <option value="VA Process Supplier">VA Process</option>
                  </select>
                }>
                {shownCats.length===0
                  ? <p style={{ color:'#94a3b8', fontSize:'13px', textAlign:'center', padding:'20px 0' }}>No product categories mapped yet</p>
                  : <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                        <thead>
                          <tr style={{ background:'#f1f5f9' }}>
                            {['Product Category','Suppliers','Capacity (pcs/d)','Registered Suppliers'].map(h=>(
                              <th key={h} style={{ padding:'10px 12px', textAlign:'left', borderBottom:'2px solid #e2e8f0', color:'#475569', fontWeight:'700' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {shownCats.map(cat=>{
                            const info=dashStats.byProdCat[cat];
                            return (
                              <tr key={cat} style={{ borderBottom:'1px solid #f1f5f9' }}>
                                <td style={{ padding:'10px 12px', fontWeight:'600', color:'#334155' }}>{cat}</td>
                                <td style={{ padding:'10px 12px', textAlign:'center' }}>
                                  <span style={{ background:'#dbeafe', color:'#1d4ed8', borderRadius:'12px', padding:'2px 10px', fontWeight:'bold', fontSize:'12px' }}>{info.count}</span>
                                </td>
                                <td style={{ padding:'10px 12px', color:'#16a34a', fontWeight:'bold', textAlign:'center' }}>{Math.round(info.cap).toLocaleString()}</td>
                                <td style={{ padding:'10px 12px' }}>
                                  <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
                                    {info.names.map(name=>{
                                      const sup=suppliers.find(s=>s.name===name);
                                      const col=TYPE_COLORS[sup?.supplierType]||'#2563eb';
                                      return (
                                        <span key={name} onClick={()=>setShowProfile(sup)}
                                          style={{ padding:'3px 10px', background:col+'12', border:`1px solid ${col}30`, borderRadius:'12px', fontSize:'11px', color:col, fontWeight:'600', cursor:'pointer' }}>
                                          {name}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                }
              </Section>

              {/* ── Monthly Capacity Bar Chart ── */}
              <Section title="Monthly Capacity Trend — All Suppliers Combined">
                {dashStats.monthlyCap.every(v=>v===0)
                  ? <p style={{ color:'#94a3b8', fontSize:'13px', textAlign:'center', padding:'20px 0' }}>Capacity data appears once sewing lines are registered</p>
                  : <>
                      {(()=>{
                        // Monthly allocated quantities from all pos
                        const monthlyAlloc = Array(12).fill(0);
                        pos.forEach(a => {
                          if (!a.startDate || !a.qty) return;
                          const mi = new Date(a.startDate).getMonth();
                          monthlyAlloc[mi] += parseFloat(a.qty)||0;
                        });
                        const maxCap = Math.max(1, ...dashStats.monthlyCap);
                        return (
                          <>
                            <div style={{ display:'flex', alignItems:'flex-end', gap:'4px', height:'130px', borderBottom:'2px solid #e2e8f0', paddingBottom:'0' }}>
                              {dashStats.monthlyCap.map((cap, i) => {
                                const allocH = cap > 0 ? Math.min(100,(monthlyAlloc[i]/cap)*100) : 0;
                                const capH   = Math.max(4,(cap/maxCap)*100);
                                const isOver = monthlyAlloc[i] > cap && cap > 0;
                                return (
                                  <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' }}>
                                    <span style={{ fontSize:'8px', color:'#94a3b8', whiteSpace:'nowrap' }}>
                                      {Math.round(cap/1000)}k
                                    </span>
                                    <div style={{ width:'100%', position:'relative', height:capH+'px', background:'#dbeafe', borderRadius:'3px 3px 0 0', overflow:'hidden' }}
                                      title={`${MONTHS_SHORT[i]}: ${Math.round(cap).toLocaleString()} capacity · ${Math.round(monthlyAlloc[i]).toLocaleString()} allocated`}>
                                      {monthlyAlloc[i]>0 && (
                                        <div style={{ position:'absolute', bottom:0, left:0, right:0,
                                          height:Math.min(100,allocH)+'%',
                                          background:isOver?'#dc2626':allocH>80?'#f59e0b':'#16a34a',
                                          borderRadius:'3px 3px 0 0' }}/>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ display:'flex', gap:'4px', paddingTop:'4px', marginBottom:'8px' }}>
                              {MONTHS_SHORT.map(m=><div key={m} style={{ flex:1, textAlign:'center', fontSize:'9px', color:'#94a3b8', fontWeight:'600' }}>{m}</div>)}
                            </div>
                            <div style={{ display:'flex', gap:'16px', fontSize:'11px', color:'#64748b' }}>
                              <span><span style={{ display:'inline-block', width:'10px', height:'10px', background:'#dbeafe', borderRadius:'2px', marginRight:'4px' }}/>Available Capacity</span>
                              <span><span style={{ display:'inline-block', width:'10px', height:'10px', background:'#16a34a', borderRadius:'2px', marginRight:'4px' }}/>Allocated (≤80%)</span>
                              <span><span style={{ display:'inline-block', width:'10px', height:'10px', background:'#f59e0b', borderRadius:'2px', marginRight:'4px' }}/>Allocated (80–100%)</span>
                              <span><span style={{ display:'inline-block', width:'10px', height:'10px', background:'#dc2626', borderRadius:'2px', marginRight:'4px' }}/>Over Capacity</span>
                            </div>
                          </>
                        );
                      })()}
                    </>
                }
              </Section>

              {/* ── Recent RFQ Activity ── */}
              {rfqItems.length>0 && (
                <Section title="Recent RFQ Activity">
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(250px,1fr))', gap:'10px' }}>
                    {rfqItems.slice(-6).reverse().map(r=>(
                      <div key={r.id} style={{ border:'1px solid #e2e8f0', padding:'10px 14px', borderRadius:'8px', background:'white' }}>
                        <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>{r.bomItem?.item||r.item}</div>
                        <div style={{ fontSize:'11px', color:'#64748b', marginTop:'3px' }}>→ {r.supplier}</div>
                        <div style={{ display:'flex', gap:'6px', marginTop:'6px', alignItems:'center' }}>
                          <Tag label={r.channel==='whatsapp'?'💬 WhatsApp':'✉ Email'} color={r.channel==='whatsapp'?'#16a34a':'#2563eb'}/>
                          <span style={{ marginLeft:'auto', fontSize:'10px', color:'#94a3b8' }}>{r.date}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {(db.factoryAudits||[]).length>0 && (
                <Section title="Top Audited Factories">
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'14px' }}>
                    {[...(db.factoryAudits||[])].sort((a,b)=>b.totalScore-a.totalScore).slice(0,3).map((a,i)=>{
                      const medals=['\uD83E\uDD47','\uD83E\uDD48','\uD83E\uDD49'];
                      const col=a.grade==='A' ? '#16a34a' : a.grade==='B' ? '#d97706' : '#dc2626';
                      const bg=a.grade==='A' ? '#f0fdf4' : a.grade==='B' ? '#fef3c7' : '#fef2f2';
                      const sup=suppliers.find(s=>s.name===a.supplierName);
                      return (
                        <div key={a.id} onClick={()=>sup&&setShowProfile(sup)}
                          style={{ background:bg, border:'2px solid '+col+'40', borderRadius:'10px', padding:'16px', cursor:sup?'pointer':'default', position:'relative' }}>
                          <div style={{ position:'absolute', top:'10px', right:'12px', fontSize:'24px' }}>{medals[i]}</div>
                          <div style={{ fontWeight:'800', fontSize:'14px', color:'#1e293b', marginBottom:'4px', paddingRight:'36px' }}>{a.supplierName}</div>
                          <div style={{ fontSize:'28px', fontWeight:'900', color:col, lineHeight:1 }}>{a.totalScore}%</div>
                          <div style={{ fontSize:'11px', color:'#64748b', margin:'4px 0' }}>Grade {a.grade} · {a.auditDate}</div>
                          <div style={{ padding:'3px 8px', background:col+'15', color:col, borderRadius:'8px', fontSize:'10px', fontWeight:'700', display:'inline-block' }}>{a.status}</div>
                        </div>
                      );
                    })}
                  </div>
                </Section>
              )}
            </div>
          );
        })()}
        {activeTab==='REGISTRATION' && (
          <div>
            {/* Type Selector + Edit Picker */}
            <div style={{ display:'flex', gap:'10px', marginBottom:'20px', flexWrap:'wrap', alignItems:'center' }}>
              {['Garment Supplier','Raw Material Supplier','VA Process Supplier'].map(t=>(
                <button key={t} onClick={()=>setSupForm({...INIT_SUP,supplierType:t})}
                  style={{ padding:'10px 20px', borderRadius:'6px', border:'1px solid #cbd5e1', background:supForm.supplierType===t?'#1e293b':'white', color:supForm.supplierType===t?'white':'#64748b', fontWeight:'bold', cursor:'pointer', fontSize:'12px' }}>
                  {t}
                </button>
              ))}
              {suppliers.length>0 && (
                <select onChange={e=>{ const s=suppliers.find(x=>String(x.id)===e.target.value); if(s) setSupForm({...INIT_SUP,...s}); }}
                  style={{ marginLeft:'auto', padding:'8px 12px', border:'1px solid #cbd5e1', borderRadius:'6px', fontSize:'12px', background:'white' }}>
                  <option value="">— Edit Existing Supplier —</option>
                  {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
            </div>

            {renderCommon()}

            {/* ── GARMENT SPECIFIC ── */}
            {supForm.supplierType==='Garment Supplier' && (
              <>
                <Section title="Product Categories & Capacity">
                  <DynamicTable
                    columns={[{label:'Category',key:'category'},{label:'Share %',key:'share'},{label:'Avg SMV',key:'smv'}]}
                    data={supForm.productCats} setData={d=>setSupForm({...supForm,productCats:d})}
                    onAdd={()=>setSupForm({...supForm,productCats:[...supForm.productCats,{category:'',share:'',smv:''}]})}/>
                </Section>

                <Section title="Factory Capacity — Line Wise">
                  <div style={{ display:'flex', gap:'15px', marginBottom:'14px', flexWrap:'wrap', alignItems:'flex-end' }}>
                    <Field label="Total Sewing Area (sq ft)" value={supForm.totalSewingArea} onChange={e=>setSupForm({...supForm,totalSewingArea:e.target.value})}/>
                    <Field label="Total Lines" type="number" value={supForm.totalLines} onChange={e=>setSupForm({...supForm,totalLines:e.target.value})} style={{ width:'100px' }}/>
                    <button onClick={()=>generateLines(supForm.totalLines)}
                      style={{ height:'34px', padding:'0 16px', background:'#2563eb', color:'white', border:'none', borderRadius:'4px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' }}>
                      Generate
                    </button>
                  </div>
                  <DynamicTable
                    columns={[{label:'Line #',key:'id'},{label:'Prod Cat',key:'category'},{label:'Machines',key:'machines'},{label:'Iron Tables',key:'iron'},{label:'QC Stations',key:'qc'},{label:'Helpers',key:'helpers'},{label:'Eff %',key:'eff'}]}
                    data={supForm.sewingLines} setData={d=>setSupForm({...supForm,sewingLines:d})} onAdd={()=>{}}/>
                  {supForm.sewingLines.length>0 && (
                    <>
                      <button onClick={()=>applyLineToAll(0)} style={{ fontSize:'11px', color:'#2563eb', border:'none', background:'none', cursor:'pointer', textDecoration:'underline', marginBottom:'8px' }}>
                        Apply Line 1 config to all lines
                      </button>
                      <div style={{ padding:'10px', background:'#f0fdf4', borderRadius:'6px', display:'flex', gap:'20px', fontSize:'12px', flexWrap:'wrap' }}>
                        <span>Total Machines: <b>{supForm.sewingLines.reduce((a,b)=>a+(parseFloat(b.machines)||0),0)}</b></span>
                        <span>Est. Total Capacity: <b style={{ color:'#16a34a' }}>{Math.round(supForm.sewingLines.reduce((acc,l)=>{
                          const smvMap = {}; (supForm.productCats||[]).forEach(pc=>{ if(pc.category) smvMap[pc.category]=parseFloat(pc.smv||0)||20; });
                          return acc+((parseFloat(l.machines)||0)*480*((parseFloat(l.eff)||0)/100)/(smvMap[(l.category||'').trim()]||20));
                        },0))} pcs/day</b></span>
                        {(()=>{
                          const smvMap={}; (supForm.productCats||[]).forEach(pc=>{ if(pc.category) smvMap[pc.category]=parseFloat(pc.smv||0)||20; });
                          const catCaps={};
                          supForm.sewingLines.forEach(l=>{ const c=(l.category||'').trim(); if(!c) return; catCaps[c]=(catCaps[c]||0)+((parseFloat(l.machines)||0)*480*((parseFloat(l.eff)||0)/100)/(smvMap[c]||20)); });
                          return Object.entries(catCaps).map(([cat,cap])=>(
                            <span key={cat} style={{ color:'#2563eb' }}>{cat}: <b>{Math.round(cap)}</b></span>
                          ));
                        })()}
                      </div>
                    </>
                  )}
                </Section>

                <Section title="Cutting & Finishing">
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>
                    <div>
                      <h5 style={{ margin:'0 0 10px', color:'#334155', fontSize:'13px' }}>Cutting</h5>
                      <Field label="Total Area"   value={supForm.cutting.area}   onChange={e=>setSupForm({...supForm,cutting:{...supForm.cutting,area:e.target.value}})}/>
                      <Field label="Total Tables" value={supForm.cutting.tables} onChange={e=>setSupForm({...supForm,cutting:{...supForm.cutting,tables:e.target.value}})} style={{ marginTop:'8px' }}/>
                      <Field label="Type" options={['Manual','Auto Spreader']} value={supForm.cutting.type} onChange={e=>setSupForm({...supForm,cutting:{...supForm.cutting,type:e.target.value}})} style={{ marginTop:'8px' }}/>
                    </div>
                    <div>
                      <h5 style={{ margin:'0 0 10px', color:'#334155', fontSize:'13px' }}>Finishing</h5>
                      <Field label="Total Area"         value={supForm.finishing.area}     onChange={e=>setSupForm({...supForm,finishing:{...supForm.finishing,area:e.target.value}})}/>
                      <Field label="Daily Capacity (pcs)" value={supForm.finishing.capacity} onChange={e=>setSupForm({...supForm,finishing:{...supForm.finishing,capacity:e.target.value}})} style={{ marginTop:'8px' }}/>
                    </div>
                  </div>
                </Section>
              </>
            )}

            {/* ── RAW MATERIAL SPECIFIC ── */}
            {supForm.supplierType==='Raw Material Supplier' && (
              <Section title="Product Categories">
                <div style={{ marginBottom:'14px', display:'flex', gap:'20px' }}>
                  <label style={{ fontSize:'13px', cursor:'pointer' }}><input type="radio" name="rm" checked={supForm.rmType==='Fabric'} onChange={()=>setSupForm({...supForm,rmType:'Fabric'})} style={{ marginRight:'5px' }}/>Fabric</label>
                  <label style={{ fontSize:'13px', cursor:'pointer' }}><input type="radio" name="rm" checked={supForm.rmType==='Trims'} onChange={()=>setSupForm({...supForm,rmType:'Trims'})} style={{ marginRight:'5px' }}/>Trims / Accessories</label>
                </div>
                <DynamicTable
                  columns={supForm.rmType==='Fabric'
                    ?[{label:'Category',key:'category'},{label:'Weave/Knit',key:'weave'},{label:'Content',key:'content'},{label:'GSM',key:'weight'},{label:'Width(in)',key:'width'},{label:'Price Range',key:'price'}]
                    :[{label:'Item Type',key:'category'},{label:'Design',key:'design'},{label:'Size Range',key:'size'},{label:'Material',key:'material'},{label:'Price Range',key:'price'}]}
                  data={supForm.productCats} setData={d=>setSupForm({...supForm,productCats:d})}
                  onAdd={()=>setSupForm({...supForm,productCats:[...supForm.productCats,{}]})}/>
              </Section>
            )}

            {/* ── VA PROCESS SPECIFIC ── */}
            {supForm.supplierType==='VA Process Supplier' && (
              <Section title="Process Capabilities">
                <Field label="Service Type" options={['Print','Embroidery','Washing','Other']} value={supForm.vaType} onChange={e=>setSupForm({...supForm,vaType:e.target.value})}/>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'15px', marginTop:'14px' }}>
                  <Field label="Technique"      value={supForm.vaDetails.technique} onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,technique:e.target.value}})}/>
                  <Field label="Mode" options={['Manual','Auto','Semi-Auto']} value={supForm.vaDetails.mode} onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,mode:e.target.value}})}/>
                  <Field label="Lead Time (days)" value={supForm.vaDetails.leadTime} onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,leadTime:e.target.value}})}/>
                </div>
                {supForm.vaType==='Print' && (
                  <div style={{ marginTop:'12px', background:'#f0fdf4', padding:'10px', borderRadius:'6px' }}>
                    <Field label="Total Tables / Machines" value={supForm.vaDetails.tablesOrMachines} onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,tablesOrMachines:e.target.value}})}/>
                  </div>
                )}
                {supForm.vaType==='Embroidery' && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'15px', marginTop:'12px', background:'#fff7ed', padding:'10px', borderRadius:'6px' }}>
                    <Field label="Handworkers"          value={supForm.vaDetails.handworkers}        onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,handworkers:e.target.value}})}/>
                    <Field label="Umbrella Machines"    value={supForm.vaDetails.umbrellaMachines}   onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,umbrellaMachines:e.target.value}})}/>
                    <Field label="Industrial Machines"  value={supForm.vaDetails.industrialMachines} onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,industrialMachines:e.target.value}})}/>
                  </div>
                )}
                {supForm.vaType==='Washing' && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'15px', marginTop:'12px', background:'#eff6ff', padding:'10px', borderRadius:'6px' }}>
                    <Field label="Wash Machines"  value={supForm.vaDetails.washingMachines} onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,washingMachines:e.target.value}})}/>
                    <Field label="Dryers"         value={supForm.vaDetails.dryingMachines}  onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,dryingMachines:e.target.value}})}/>
                    <Field label="PP Stations"    value={supForm.vaDetails.ppStations}      onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,ppStations:e.target.value}})}/>
                    <Field label="Laser Machines" value={supForm.vaDetails.laserMachines}   onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,laserMachines:e.target.value}})}/>
                  </div>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginTop:'12px' }}>
                  <Field label="Sample Capacity/Day" value={supForm.vaDetails.sampleCap} onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,sampleCap:e.target.value}})}/>
                  <Field label="Prod Capacity/Day"   value={supForm.vaDetails.prodCap}   onChange={e=>setSupForm({...supForm,vaDetails:{...supForm.vaDetails,prodCap:e.target.value}})}/>
                </div>
              </Section>
            )}

            {renderEvaluation()}
            {renderCalendar()}

            <button onClick={handleSaveSupplier}
              style={{ width:'100%', padding:'15px', background:'#16a34a', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', fontSize:'15px', cursor:'pointer', marginTop:'10px', letterSpacing:'0.05em' }}>
              ✓ SAVE SUPPLIER PROFILE
            </button>
          </div>
        )}

        {/* ━━━━━━━━━━━━━━━━━━━ SUPPLIER PROFILE MODAL ━━━━━━━━━━━━━━━━━━━ */}
        {showProfile && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
            onClick={e=>e.target===e.currentTarget&&setShowProfile(null)}>
            <div style={{ background:'white', borderRadius:'12px', width:'100%', maxWidth:'860px', maxHeight:'90vh', overflow:'auto', boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }}>

              {/* Header */}
              <div style={{ background:'linear-gradient(135deg,#1e293b,#334155)', padding:'20px 24px', borderRadius:'12px 12px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                  {showProfile.images?.companyLogo
                    ? <img src={showProfile.images.companyLogo} alt="logo" style={{ height:'44px', width:'44px', borderRadius:'8px', objectFit:'contain', background:'white', padding:'4px' }} onError={e=>e.target.style.display='none'}/>
                    : <div style={{ width:'44px', height:'44px', background:'#2563eb', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center' }}><Factory size={22} color="white"/></div>
                  }
                  <div>
                    <div style={{ fontWeight:'800', fontSize:'18px', color:'white' }}>{showProfile.name}</div>
                    <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'2px' }}>{showProfile.supplierType} · {showProfile.address?.city}, {showProfile.address?.country}</div>
                  </div>
                </div>
                <button onClick={()=>setShowProfile(null)} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'white', cursor:'pointer', borderRadius:'6px', padding:'6px' }}><X size={18}/></button>
              </div>

              <div style={{ padding:'20px 24px' }}>

                {(()=>{
                  const allAudits = db.factoryAudits || [];
                  const supAudit = [...allAudits].filter(a=>a.supplierName===showProfile.name).sort((a,b)=>b.totalScore-a.totalScore)[0];
                  if (!supAudit) return null;
                  const topThree = [...allAudits].sort((a,b)=>b.totalScore-a.totalScore).slice(0,3).map(a=>a.supplierName);
                  const rank = topThree.indexOf(showProfile.name);
                  const medals = ['\uD83E\uDD47','\uD83E\uDD48','\uD83E\uDD49'];
                  const statusCol = supAudit.grade==='A' ? '#16a34a' : supAudit.grade==='B' ? '#d97706' : '#dc2626';
                  const statusBg  = supAudit.grade==='A' ? '#f0fdf4'  : supAudit.grade==='B' ? '#fef3c7'  : '#fef2f2';
                  return (
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 14px', background:statusBg, border:'1.5px solid '+statusCol+'40', borderRadius:'10px', flexWrap:'wrap', marginBottom:'14px' }}>
                      {rank>=0 && <span style={{ fontSize:'20px' }}>{medals[rank]}</span>}
                      <div>
                        <div style={{ fontSize:'10px', color:'#64748b', fontWeight:'700' }}>AUDIT SCORE</div>
                        <div style={{ fontSize:'18px', fontWeight:'900', color:statusCol, lineHeight:1 }}>{supAudit.totalScore}%</div>
                      </div>
                      <div style={{ borderLeft:'1px solid #e2e8f0', paddingLeft:'10px' }}>
                        <div style={{ fontSize:'10px', color:'#64748b', fontWeight:'700' }}>GRADE</div>
                        <div style={{ fontSize:'16px', fontWeight:'900', color:statusCol }}>{supAudit.grade}</div>
                      </div>
                      <div style={{ borderLeft:'1px solid #e2e8f0', paddingLeft:'10px' }}>
                        <div style={{ fontSize:'10px', color:'#64748b', fontWeight:'700' }}>STATUS</div>
                        <div style={{ padding:'2px 8px', background:statusCol+'15', color:statusCol, borderRadius:'8px', fontSize:'10px', fontWeight:'800' }}>{supAudit.status}</div>
                      </div>
                      {supAudit.capRequired && (
                        <div style={{ borderLeft:'1px solid #e2e8f0', paddingLeft:'10px' }}>
                          <div style={{ fontSize:'10px', background:'#ede9fe', color:'#7c3aed', padding:'2px 8px', borderRadius:'8px', fontWeight:'700' }}>CAP Required</div>
                          <div style={{ fontSize:'9px', color:'#94a3b8', marginTop:'2px' }}>Audited: {supAudit.auditDate}</div>
                        </div>
                      )}
                      {rank>=0 && (
                        <div style={{ marginLeft:'auto', fontSize:'10px', background:'#fef3c7', color:'#92400e', padding:'3px 8px', borderRadius:'8px', fontWeight:'800' }}>
                          {medals[rank]} Top #{rank+1} Factory
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div style={{ display:'flex', gap:'10px', marginBottom:'18px', flexWrap:'wrap' }}>
                  {(showProfile.evaluation?.sustScore>0) && (
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', background:'#f0fdf4', border:'1px solid #bbf7d0', padding:'6px 12px', borderRadius:'8px' }}>
                      <Leaf size={13} color="#16a34a"/>
                      <span style={{ fontSize:'11px', fontWeight:'bold', color:'#15803d' }}>Sustainability</span>
                      <RatingStars value={showProfile.evaluation.sustScore}/>
                    </div>
                  )}
                  {(showProfile.evaluation?.qualityScore>0) && (
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', background:'#eff6ff', border:'1px solid #bfdbfe', padding:'6px 12px', borderRadius:'8px' }}>
                      <Shield size={13} color="#2563eb"/>
                      <span style={{ fontSize:'11px', fontWeight:'bold', color:'#1d4ed8' }}>Quality</span>
                      <RatingStars value={showProfile.evaluation.qualityScore}/>
                    </div>
                  )}
                  {[...(showProfile.evaluation?.certifications||[]),...(showProfile.evaluation?.qualityCerts||[])].map(c=>(
                    <Tag key={c} label={c} color="#7c3aed"/>
                  ))}
                  {showProfile.totalCapacity>0 && <Tag label={`${Math.round(showProfile.totalCapacity)} pcs/day`} color="#d97706"/>}
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>
                  {/* LEFT: Contact + Maps */}
                  <div>
                    <div style={{ background:'#f8fafc', padding:'14px', borderRadius:'8px', marginBottom:'14px' }}>
                      <div style={{ fontWeight:'700', fontSize:'12px', color:'#334155', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Contact Details</div>
                      {showProfile.contact?.phone && <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'7px', fontSize:'12px' }}><Phone size={13} color="#64748b"/>{showProfile.contact.phone}</div>}
                      {showProfile.contact?.mobile && (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'7px', fontSize:'12px' }}>
                          <MessageCircle size={13} color="#16a34a"/>
                          <a href={`https://wa.me/${showProfile.contact.mobile.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ color:'#16a34a', textDecoration:'none' }}>{showProfile.contact.mobile}</a>
                        </div>
                      )}
                      {showProfile.contact?.email && (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'7px', fontSize:'12px' }}>
                          <Mail size={13} color="#2563eb"/>
                          <a href={`mailto:${showProfile.contact.email}`} style={{ color:'#2563eb', textDecoration:'none' }}>{showProfile.contact.email}</a>
                        </div>
                      )}
                      {showProfile.contact?.website && (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'7px', fontSize:'12px' }}>
                          <Globe size={13} color="#64748b"/>
                          <a href={showProfile.contact.website.startsWith('http')?showProfile.contact.website:'https://'+showProfile.contact.website} target="_blank" rel="noreferrer" style={{ color:'#2563eb', textDecoration:'none' }}>{showProfile.contact.website}</a>
                        </div>
                      )}
                      {showProfile.contact?.linkedin && (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'7px', fontSize:'12px' }}>
                          <ExternalLink size={13} color="#0a66c2"/>
                          <a href={showProfile.contact.linkedin} target="_blank" rel="noreferrer" style={{ color:'#0a66c2', textDecoration:'none' }}>LinkedIn Profile</a>
                        </div>
                      )}
                      {showProfile.contact?.instagram && (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px' }}>
                          <ImageIcon size={13} color="#e1306c"/>
                          <a href={`https://instagram.com/${showProfile.contact.instagram.replace('@','')}`} target="_blank" rel="noreferrer" style={{ color:'#e1306c', textDecoration:'none' }}>{showProfile.contact.instagram}</a>
                        </div>
                      )}
                    </div>

                    <div style={{ marginBottom:'14px' }}>
                      <div style={{ fontWeight:'700', fontSize:'11px', color:'#64748b', marginBottom:'6px', textTransform:'uppercase' }}>📍 Head Office — {showProfile.address?.city}</div>
                      <MapEmbed lat={showProfile.address?.lat} lng={showProfile.address?.lng}/>
                    </div>
                    <div>
                      <div style={{ fontWeight:'700', fontSize:'11px', color:'#64748b', marginBottom:'6px', textTransform:'uppercase' }}>🏭 Factory — {showProfile.factoryAddress?.city}</div>
                      <MapEmbed lat={showProfile.factoryAddress?.lat} lng={showProfile.factoryAddress?.lng}/>
                    </div>
                  </div>

                  {/* RIGHT: Images + Capacity */}
                  <div>
                    {(showProfile.images?.companyImages||[]).filter(Boolean).length>0 && (
                      <div style={{ marginBottom:'14px' }}>
                        <div style={{ fontWeight:'700', fontSize:'11px', color:'#64748b', marginBottom:'8px', textTransform:'uppercase' }}>Company Gallery</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'6px' }}>
                          {showProfile.images.companyImages.filter(Boolean).map((url,i)=>(
                            <img key={i} src={url} alt="" style={{ width:'100%', height:'90px', objectFit:'cover', borderRadius:'6px', border:'1px solid #e2e8f0' }} onError={e=>e.target.style.display='none'}/>
                          ))}
                        </div>
                      </div>
                    )}
                    {(showProfile.images?.factoryImages||[]).filter(Boolean).length>0 && (
                      <div style={{ marginBottom:'14px' }}>
                        <div style={{ fontWeight:'700', fontSize:'11px', color:'#64748b', marginBottom:'8px', textTransform:'uppercase' }}>Factory Gallery</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'6px' }}>
                          {showProfile.images.factoryImages.filter(Boolean).map((url,i)=>(
                            <img key={i} src={url} alt="" style={{ width:'100%', height:'90px', objectFit:'cover', borderRadius:'6px', border:'1px solid #e2e8f0' }} onError={e=>e.target.style.display='none'}/>
                          ))}
                        </div>
                      </div>
                    )}
                    {showProfile.supplierType==='Garment Supplier' && showProfile.sewingLines?.length>0 && (
                      <div style={{ background:'#f0fdf4', padding:'12px', borderRadius:'8px', marginBottom:'14px' }}>
                        <div style={{ fontWeight:'700', fontSize:'11px', color:'#15803d', marginBottom:'8px', textTransform:'uppercase' }}>Capacity Summary</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', fontSize:'12px' }}>
                          <div>Lines: <b>{showProfile.sewingLines.length}</b></div>
                          <div>Machines: <b>{showProfile.machineTotal}</b></div>
                          <div>Avg Eff: <b>{parseFloat(showProfile.factoryEff||0).toFixed(1)}%</b></div>
                          <div>Daily Cap: <b style={{ color:'#16a34a' }}>{Math.round(showProfile.totalCapacity)} pcs</b></div>
                        </div>
                      </div>
                    )}
                    {(showProfile.productCats||[]).length>0 && (
                      <div style={{ marginBottom:'14px' }}>
                        <div style={{ fontWeight:'700', fontSize:'11px', color:'#64748b', marginBottom:'8px', textTransform:'uppercase' }}>Product Categories</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
                          {showProfile.productCats.map((pc,i)=><Tag key={i} label={pc.category||pc.name} color="#2563eb"/>)}
                        </div>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:'8px', marginTop:'12px' }}>
                      <button onClick={()=>{ setSupForm({...INIT_SUP,...showProfile}); setShowProfile(null); setActiveTab('REGISTRATION'); }}
                        style={{ flex:1, padding:'8px', background:'#1e293b', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>
                        ✏ Edit Profile
                      </button>
                      <button onClick={()=>{ setShowProfile(null); setActiveTab('SOURCING BOM'); }}
                        style={{ flex:1, padding:'8px', background:'#2563eb', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>
                        📩 Send RFQ
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━ PRODUCTS ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {activeTab==='PRODUCTS' && <VALibrary db={db} setDb={setDb} saveToDB={saveToDB}/>}

        {/* ━━━━━━━━━━━━━━━━━━━━━━━ SOURCING BOM ━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {activeTab==='SOURCING BOM' && (()=>{
          const styles=(db.mgmtStyles||[]).map(s=>s.styleRef).filter(Boolean);
          const bomItems=selectedStyle&&selectedStyle!=='--Select Style--' ? fetchBomForStyle(selectedStyle) : sourcingBom;
          const planLines=selectedStyle ? getPlanningLines(selectedStyle) : [];
          const styleObj=(db.mgmtStyles||[]).find(s=>s.styleRef===selectedStyle);

          return (
            <div>
              {/* Style selector row */}
              <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:'20px', marginBottom:'20px' }}>
                <Section title="1. Select Style">
                  <Field label="Style Reference" value={selectedStyle}
                    onChange={e=>setSelectedStyle(e.target.value)}
                    options={['--Select Style--',...styles]}/>
                  {styleObj && (
                    <div style={{ marginTop:'12px', padding:'10px', background:'#f0fdf4', borderRadius:'6px', border:'1px solid #86efac', fontSize:'12px' }}>
                      <div style={{ fontWeight:'bold', color:'#15803d', marginBottom:'4px' }}>✓ {selectedStyle}</div>
                      {styleObj.buyer   && <div style={{ color:'#166534' }}>Buyer: {styleObj.buyer}</div>}
                      {styleObj.season  && <div style={{ color:'#166534' }}>Season: {styleObj.season}</div>}
                      <div style={{ color:'#166534', marginTop:'4px' }}>BOM items: {bomItems.length}</div>
                    </div>
                  )}
                  {styles.length===0 && (
                    <div style={{ marginTop:'10px', padding:'10px', background:'#fef3c7', borderRadius:'6px', fontSize:'11px', color:'#92400e' }}>
                      ⚠ No styles in Style Registry
                    </div>
                  )}
                </Section>

                {/* Planning lines from ProductionPlanning */}
                {planLines.length>0 && (
                  <Section title="Line Capacity from Planning Module">
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                        <thead><tr style={{ background:'#f1f5f9' }}>
                          {['Line','Category','Machines','Eff %','Daily Cap'].map(h=><th key={h} style={{ padding:'8px', textAlign:'left', color:'#475569' }}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {planLines.map((l,i)=>(
                            <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                              <td style={{ padding:'7px 8px' }}>Line {l.id||i+1}</td>
                              <td style={{ padding:'7px 8px' }}>{l.category||'-'}</td>
                              <td style={{ padding:'7px 8px' }}>{l.machines||'-'}</td>
                              <td style={{ padding:'7px 8px' }}>{l.eff||'-'}%</td>
                              <td style={{ padding:'7px 8px', color:'#16a34a', fontWeight:'bold' }}>
                                {l.machines&&l.eff ? Math.round((parseFloat(l.machines)*480*(parseFloat(l.eff)/100))/20) : '-'} pcs
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                )}
              </div>

              {/* BOM Item List */}
              <Section title="2. BoM Items — Supplier Matching & RFQ Dispatch"
                actions={<span style={{ fontSize:'11px', color:'#94a3b8' }}>{bomItems.length} items</span>}>
                {bomItems.length===0
                  ? <p style={{ color:'#94a3b8', textAlign:'center', padding:'30px', fontSize:'13px' }}>
                      {selectedStyle&&selectedStyle!=='--Select Style--' ? 'No BOM items found for this style. Add BOM in Style Registry.' : 'Select a style to load BOM items.'}
                    </p>
                  : <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                        <thead>
                          <tr style={{ background:'#f1f5f9' }}>
                            {['#','Item','Type','Qty / Unit','Composition / Spec','Matched Suppliers','RFQ Sent','Actions'].map(h=>(
                              <th key={h} style={{ padding:'10px 12px', textAlign:'left', borderBottom:'2px solid #e2e8f0', color:'#475569', fontWeight:'700', whiteSpace:'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {bomItems.map((bom,idx)=>{
                            const matched=getMatchedSuppliers(bom);
                            const sent=rfqItems.filter(r=>r.styleRef===selectedStyle&&(r.bomItem?.id===bom.id||r.bomItem?.item===bom.item));
                            return (
                              <tr key={bom.id||idx} style={{ borderBottom:'1px solid #f1f5f9' }}>
                                <td style={{ padding:'10px 12px', color:'#94a3b8' }}>{idx+1}</td>
                                <td style={{ padding:'10px 12px', fontWeight:'700', color:'#1e293b' }}>{bom.item}</td>
                                <td style={{ padding:'10px 12px' }}>
                                  <span style={{ background:'#f1f5f9', padding:'2px 8px', borderRadius:'12px', fontSize:'10px', fontWeight:'600', color:'#475569' }}>{bom.type}</span>
                                </td>
                                <td style={{ padding:'10px 12px' }}>{bom.qty} {bom.unit}</td>
                                <td style={{ padding:'10px 12px', color:'#64748b', fontSize:'11px', maxWidth:'150px' }}>
                                  {[bom.composition, bom.width&&`W:${bom.width}`, bom.gsm&&`GSM:${bom.gsm}`].filter(Boolean).join(' · ')||'-'}
                                </td>
                                <td style={{ padding:'10px 12px' }}>
                                  {matched.length>0
                                    ? <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                                        {matched.slice(0,3).map(s=>(
                                          <span key={s.id} onClick={()=>setShowProfile(s)}
                                            style={{ fontSize:'10px', padding:'2px 7px', background:'#dbeafe', color:'#1d4ed8', borderRadius:'10px', cursor:'pointer', fontWeight:'600' }}>{s.name}</span>
                                        ))}
                                        {matched.length>3 && <span style={{ fontSize:'10px', color:'#94a3b8' }}>+{matched.length-3}</span>}
                                      </div>
                                    : <span style={{ fontSize:'10px', color:'#f59e0b', background:'#fef3c7', padding:'2px 7px', borderRadius:'10px' }}>No match</span>
                                  }
                                </td>
                                <td style={{ padding:'10px 12px' }}>
                                  {sent.length>0
                                    ? <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                                        {sent.map(r=><span key={r.id} style={{ fontSize:'10px', color:'#16a34a' }}>✓ {r.supplier}</span>)}
                                      </div>
                                    : <span style={{ fontSize:'10px', color:'#94a3b8' }}>Not sent</span>
                                  }
                                </td>
                                <td style={{ padding:'10px 12px' }}>
                                  <button onClick={()=>setRfqModal({bomItem:bom,matched})}
                                    style={{ background:'#2563eb', color:'white', border:'none', padding:'5px 10px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', fontWeight:'600', whiteSpace:'nowrap' }}>
                                    📩 Send RFQ
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                }
              </Section>

              {/* RFQ Log */}
              {rfqItems.filter(r=>r.styleRef===selectedStyle).length>0 && (
                <Section title="RFQ Dispatch Log — This Style">
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                    <thead><tr style={{ background:'#f1f5f9' }}>
                      {['Item','Supplier','Channel','Date','Status'].map(h=>(
                        <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#475569' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {rfqItems.filter(r=>r.styleRef===selectedStyle).map(r=>(
                        <tr key={r.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                          <td style={{ padding:'8px 10px', fontWeight:'600' }}>{r.bomItem?.item||r.item}</td>
                          <td style={{ padding:'8px 10px' }}>{r.supplier}</td>
                          <td style={{ padding:'8px 10px' }}>{r.channel==='whatsapp'?'💬 WhatsApp':'✉ Email'}</td>
                          <td style={{ padding:'8px 10px', color:'#94a3b8' }}>{r.date}</td>
                          <td style={{ padding:'8px 10px' }}><Tag label={r.status} color="#16a34a"/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              )}
            </div>
          );
        })()}

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━ RFQ MODAL ━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {rfqModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
            onClick={e=>e.target===e.currentTarget&&setRfqModal(null)}>
            <div style={{ background:'white', borderRadius:'12px', width:'100%', maxWidth:'640px', maxHeight:'90vh', overflow:'auto', boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ background:'#1e293b', padding:'16px 20px', borderRadius:'12px 12px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:'800', color:'white', fontSize:'15px' }}>📩 Send RFQ</div>
                  <div style={{ color:'#94a3b8', fontSize:'11px', marginTop:'2px' }}>{rfqModal.bomItem?.item}</div>
                </div>
                <button onClick={()=>setRfqModal(null)} style={{ background:'none', border:'none', color:'white', cursor:'pointer' }}><X size={18}/></button>
              </div>

              <div style={{ padding:'20px' }}>
                {/* Item Details */}
                <div style={{ background:'#f8fafc', padding:'12px', borderRadius:'8px', marginBottom:'16px', border:'1px solid #e2e8f0' }}>
                  <div style={{ fontWeight:'700', fontSize:'11px', color:'#334155', marginBottom:'8px', textTransform:'uppercase' }}>Item Details (sent to supplier)</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'12px' }}>
                    <div><span style={{ color:'#64748b' }}>Item:</span> <b>{rfqModal.bomItem?.item}</b></div>
                    <div><span style={{ color:'#64748b' }}>Type:</span> <b>{rfqModal.bomItem?.type}</b></div>
                    <div><span style={{ color:'#64748b' }}>Qty:</span> <b>{rfqModal.bomItem?.qty} {rfqModal.bomItem?.unit}</b></div>
                    {rfqModal.bomItem?.composition && <div><span style={{ color:'#64748b' }}>Composition:</span> <b>{rfqModal.bomItem.composition}</b></div>}
                    {rfqModal.bomItem?.width && <div><span style={{ color:'#64748b' }}>Width:</span> <b>{rfqModal.bomItem.width}</b></div>}
                    {rfqModal.bomItem?.gsm   && <div><span style={{ color:'#64748b' }}>GSM:</span> <b>{rfqModal.bomItem.gsm}</b></div>}
                    {rfqModal.bomItem?.description && <div style={{ gridColumn:'1/-1' }}><span style={{ color:'#64748b' }}>Spec:</span> <b>{rfqModal.bomItem.description}</b></div>}
                  </div>
                </div>

                <div style={{ fontWeight:'700', fontSize:'12px', color:'#334155', marginBottom:'10px' }}>
                  Select Suppliers — {rfqModal.matched?.length||0} matched · {suppliers.length} total
                </div>
                {rfqModal.matched?.length===0 && (
                  <p style={{ color:'#f59e0b', background:'#fef3c7', padding:'10px', borderRadius:'6px', fontSize:'12px', marginBottom:'10px' }}>
                    No matched suppliers for this type. Showing all suppliers.
                  </p>
                )}

                <div style={{ display:'flex', flexDirection:'column', gap:'8px', maxHeight:'300px', overflow:'auto' }}>
                  {(rfqModal.matched?.length>0 ? rfqModal.matched : suppliers).map(sup=>(
                    <div key={sup.id} style={{ border:'1px solid #e2e8f0', padding:'12px', borderRadius:'8px', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fafafa' }}>
                      <div>
                        <div style={{ fontWeight:'700', fontSize:'13px', color:'#1e293b' }}>{sup.name}</div>
                        <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>
                          {sup.address?.city} · {sup.supplierType}
                          {sup.contact?.email&&` · ${sup.contact.email}`}
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:'6px' }}>
                        <button onClick={()=>dispatchRFQ(rfqModal.bomItem,sup,'whatsapp')}
                          disabled={!sup.contact?.whatsapp&&!sup.contact?.mobile}
                          style={{ padding:'6px 12px', background:(!sup.contact?.whatsapp&&!sup.contact?.mobile)?'#e2e8f0':'#16a34a', color:(!sup.contact?.whatsapp&&!sup.contact?.mobile)?'#94a3b8':'white', border:'none', borderRadius:'5px', fontSize:'11px', cursor:(!sup.contact?.whatsapp&&!sup.contact?.mobile)?'not-allowed':'pointer', fontWeight:'600' }}>
                          💬 WhatsApp
                        </button>
                        <button onClick={()=>dispatchRFQ(rfqModal.bomItem,sup,'email')}
                          disabled={!sup.contact?.email}
                          style={{ padding:'6px 12px', background:!sup.contact?.email?'#e2e8f0':'#2563eb', color:!sup.contact?.email?'#94a3b8':'white', border:'none', borderRadius:'5px', fontSize:'11px', cursor:!sup.contact?.email?'not-allowed':'pointer', fontWeight:'600' }}>
                          ✉ Email
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop:'16px', display:'flex', gap:'8px' }}>
                  <button onClick={()=>{ (rfqModal.matched?.length>0?rfqModal.matched:suppliers).forEach(s=>dispatchRFQ(rfqModal.bomItem,s,'email')); }}
                    style={{ flex:1, padding:'10px', background:'#f1f5f9', color:'#334155', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'12px' }}>
                    ✉ Email to All Matched
                  </button>
                  <button onClick={()=>setRfqModal(null)}
                    style={{ padding:'10px 20px', background:'white', color:'#64748b', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'12px' }}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab==='QUOTATIONS' && (()=>{
          // Group quotes by item, sorted cheapest first
          const byItem=quotations.reduce((acc,q)=>{
            const key=q.item||'Unknown';
            if(!acc[key]) acc[key]=[];
            acc[key].push(q);
            return acc;
          },{});
          Object.values(byItem).forEach(arr=>arr.sort((a,b)=>parseFloat(a.price)-parseFloat(b.price)));

          return (
            <div>
              {/* ── Log Quote ── */}
              <Section title="Log Received Quotation"
                actions={<span style={{ fontSize:'11px', color:'#94a3b8' }}>Enter quotes received via WhatsApp · Email · Template</span>}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))', gap:'10px' }}>
                  <div>
                    <label style={{ fontSize:'11px', fontWeight:'bold', color:'#64748b' }}>Supplier *</label>
                    <select value={quoteInput.supplier} onChange={e=>setQuoteInput({...quoteInput,supplier:e.target.value})}
                      style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px', marginTop:'4px' }}>
                      <option value="">— Select —</option>
                      {suppliers.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:'11px', fontWeight:'bold', color:'#64748b' }}>Item *</label>
                    <input value={quoteInput.item} onChange={e=>setQuoteInput({...quoteInput,item:e.target.value})} placeholder="e.g. Cotton Twill"
                      style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px', marginTop:'4px', boxSizing:'border-box' }}/>
                  </div>
                  <Field label="Price *"     value={quoteInput.price}    onChange={e=>setQuoteInput({...quoteInput,price:e.target.value})}    placeholder="0.00"/>
                  <Field label="Unit"        value={quoteInput.unit}     onChange={e=>setQuoteInput({...quoteInput,unit:e.target.value})}     placeholder="Mtr / Pcs"/>
                  <Field label="MOQ"         value={quoteInput.moq}      onChange={e=>setQuoteInput({...quoteInput,moq:e.target.value})}      placeholder="500 Mtr"/>
                  <Field label="Lead Time"   value={quoteInput.leadTime} onChange={e=>setQuoteInput({...quoteInput,leadTime:e.target.value})} placeholder="30 days"/>
                </div>
                <div style={{ marginTop:'10px', display:'flex', gap:'10px', alignItems:'flex-end' }}>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:'11px', fontWeight:'bold', color:'#64748b' }}>Notes / Payment Terms</label>
                    <input value={quoteInput.notes} onChange={e=>setQuoteInput({...quoteInput,notes:e.target.value})} placeholder="e.g. LC 30 days, FOB Jaipur"
                      style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px', marginTop:'4px', boxSizing:'border-box' }}/>
                  </div>
                  <button onClick={handleSaveQuote}
                    style={{ padding:'8px 20px', background:'#16a34a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px', height:'36px', whiteSpace:'nowrap' }}>
                    + Log Quote
                  </button>
                </div>
              </Section>

              {/* ── Quotation Summary Table ── */}
              {Object.keys(byItem).length>0 && (
                <Section title="Quotation Summary — All Items × All Suppliers"
                  actions={<span style={{ fontSize:'11px', background:'#dbeafe', color:'#1d4ed8', padding:'3px 8px', borderRadius:'4px', fontWeight:'bold' }}>{quotations.length} quotes</span>}>
                  {Object.entries(byItem).map(([item,quotes])=>(
                    <div key={item} style={{ marginBottom:'22px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                        <div style={{ fontWeight:'800', fontSize:'14px', color:'#1e293b' }}>{item}</div>
                        <div style={{ fontSize:'11px', background:'#f0fdf4', color:'#16a34a', padding:'2px 8px', borderRadius:'10px', fontWeight:'600' }}>
                          Best: ${parseFloat(quotes[0]?.price||0).toFixed(2)} ({quotes[0]?.supplier})
                        </div>
                        <span style={{ fontSize:'11px', color:'#94a3b8', marginLeft:'auto' }}>{quotes.length} quote{quotes.length!==1?'s':''}</span>
                      </div>
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                          <thead>
                            <tr style={{ background:'#f8fafc' }}>
                              {['Supplier','Price','Unit','MOQ','Lead Time','Notes','Date','Select for FOB'].map(h=>(
                                <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#475569', fontWeight:'700', fontSize:'11px', borderBottom:'1px solid #e2e8f0', whiteSpace:'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {quotes.map((q,i)=>{
                              const isBest=i===0;
                              const isSelected=fobSelections[item]?.id===q.id;
                              return (
                                <tr key={q.id} style={{ borderBottom:'1px solid #f1f5f9', background:isSelected?'#f0fdf4':isBest?'#fafff8':'white' }}>
                                  <td style={{ padding:'8px 10px', fontWeight:'600', color:'#334155' }}>{q.supplier}</td>
                                  <td style={{ padding:'8px 10px' }}>
                                    <span style={{ fontWeight:'bold', color:isBest?'#16a34a':'#1e293b', fontSize:'13px' }}>${parseFloat(q.price).toFixed(2)}</span>
                                    {isBest && <span style={{ marginLeft:'5px', fontSize:'9px', background:'#dcfce7', color:'#16a34a', padding:'1px 5px', borderRadius:'8px', fontWeight:'700' }}>BEST</span>}
                                  </td>
                                  <td style={{ padding:'8px 10px', color:'#64748b' }}>{q.unit||'-'}</td>
                                  <td style={{ padding:'8px 10px', color:'#64748b' }}>{q.moq||'-'}</td>
                                  <td style={{ padding:'8px 10px', color:'#64748b' }}>{q.leadTime||'-'}</td>
                                  <td style={{ padding:'8px 10px', color:'#64748b', fontSize:'11px' }}>{q.notes||'-'}</td>
                                  <td style={{ padding:'8px 10px', color:'#94a3b8', fontSize:'11px' }}>{q.date}</td>
                                  <td style={{ padding:'8px 10px' }}>
                                    <button onClick={()=>setFobSelections(prev=>({...prev,[item]:isSelected?undefined:q}))}
                                      style={{ padding:'5px 10px', background:isSelected?'#16a34a':'white', color:isSelected?'white':'#64748b', border:`1px solid ${isSelected?'#16a34a':'#e2e8f0'}`, borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
                                      {isSelected?'✓ Selected':'Select'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              {/* ── FOB Builder ── */}
              <Section title="FOB Builder — Assemble from Selected Supplier Prices">
                {Object.keys(fobSelections).filter(k=>fobSelections[k]).length===0
                  ? <p style={{ color:'#94a3b8', textAlign:'center', padding:'20px', fontSize:'13px' }}>
                      Select quotes from the summary above to build your FOB costing.
                    </p>
                  : <div>
                      <div style={{ overflowX:'auto', marginBottom:'16px' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                          <thead>
                            <tr style={{ background:'#f1f5f9' }}>
                              {['Item','Supplier','Price','Unit','Lead Time','Notes',''].map(h=>(
                                <th key={h} style={{ padding:'10px 12px', textAlign:'left', color:'#475569', fontWeight:'700' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(fobSelections).filter(([,v])=>v).map(([item,q])=>(
                              <tr key={item} style={{ borderBottom:'1px solid #f1f5f9' }}>
                                <td style={{ padding:'10px 12px', fontWeight:'700', color:'#1e293b' }}>{item}</td>
                                <td style={{ padding:'10px 12px' }}>{q.supplier}</td>
                                <td style={{ padding:'10px 12px', color:'#16a34a', fontWeight:'bold' }}>${parseFloat(q.price).toFixed(2)}</td>
                                <td style={{ padding:'10px 12px', color:'#64748b' }}>{q.unit||'-'}</td>
                                <td style={{ padding:'10px 12px', color:'#64748b' }}>{q.leadTime||'-'}</td>
                                <td style={{ padding:'10px 12px', color:'#64748b', fontSize:'11px' }}>{q.notes||'-'}</td>
                                <td style={{ padding:'10px 12px' }}>
                                  <button onClick={()=>setFobSelections(prev=>{ const n={...prev}; delete n[item]; return n; })}
                                    style={{ color:'#ef4444', border:'none', background:'none', cursor:'pointer' }}><Trash2 size={13}/></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background:'#f8fafc', borderTop:'2px solid #e2e8f0' }}>
                              <td colSpan={2} style={{ padding:'10px 12px', fontWeight:'bold', color:'#334155' }}>Calculated Total FOB</td>
                              <td colSpan={5} style={{ padding:'10px 12px', fontWeight:'bold', color:'#16a34a', fontSize:'16px' }}>${calcFobTotal()}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:'15px', marginBottom:'16px' }}>
                        <div>
                          <label style={{ fontSize:'11px', fontWeight:'bold', color:'#64748b' }}>Manual FOB Override</label>
                          <input value={manualFob} onChange={e=>setManualFob(e.target.value)} placeholder={`$${calcFobTotal()}`}
                            style={{ width:'100%', padding:'8px', border:'2px solid #2563eb', borderRadius:'4px', fontSize:'14px', fontWeight:'bold', color:'#1e293b', marginTop:'4px', boxSizing:'border-box' }}/>
                        </div>
                        <div>
                          <label style={{ fontSize:'11px', fontWeight:'bold', color:'#64748b' }}>Comments / Revision Notes</label>
                          <textarea value={fobComment} onChange={e=>setFobComment(e.target.value)} placeholder="e.g. 5% buffer added on fabric..."
                            style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px', marginTop:'4px', boxSizing:'border-box', height:'58px', resize:'vertical' }}></textarea>
                        </div>
                      </div>

                      <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                        <div style={{ flex:1, padding:'12px 16px', background:'linear-gradient(135deg,#f0fdf4,#dcfce7)', border:'2px solid #16a34a', borderRadius:'8px', display:'flex', alignItems:'center', gap:'10px' }}>
                          <DollarSign size={20} color="#16a34a"/>
                          <div>
                            <div style={{ fontSize:'11px', color:'#64748b' }}>Final FOB</div>
                            <div style={{ fontSize:'22px', fontWeight:'800', color:'#16a34a' }}>${manualFob||calcFobTotal()}</div>
                          </div>
                        </div>
                        <button onClick={handleSaveFOB}
                          style={{ padding:'14px 28px', background:'#1e293b', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'bold', fontSize:'13px' }}>
                          💾 Save FOB Costing
                        </button>
                      </div>
                    </div>
                }
              </Section>

              {/* ── Saved FOBs ── */}
              {savedFobs.length>0 && (
                <Section title="Saved FOB Costings">
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                      <thead>
                        <tr style={{ background:'#f1f5f9' }}>
                          {['Style','Items','Total FOB','Date','Status','Comment','Actions'].map(h=>(
                            <th key={h} style={{ padding:'10px 12px', textAlign:'left', color:'#475569', fontWeight:'700' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {savedFobs.map(fob=>(
                          <tr key={fob.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                            <td style={{ padding:'10px 12px', fontWeight:'600' }}>{fob.styleRef||'-'}</td>
                            <td style={{ padding:'10px 12px', color:'#64748b', fontSize:'11px', maxWidth:'160px' }}>{fob.items?.map(i=>i.item).join(', ')||'-'}</td>
                            <td style={{ padding:'10px 12px', fontWeight:'bold', color:'#16a34a', fontSize:'14px' }}>${fob.totalFob}</td>
                            <td style={{ padding:'10px 12px', color:'#94a3b8' }}>{fob.date}</td>
                            <td style={{ padding:'10px 12px' }}><Tag label={fob.status} color={fob.status==='Approved'?'#16a34a':'#d97706'}/></td>
                            <td style={{ padding:'10px 12px', color:'#64748b', fontSize:'11px' }}>{fob.comment||'-'}</td>
                            <td style={{ padding:'10px 12px' }}>
                              <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                                {fob.status!=='Approved' && (
                                  <button onClick={()=>handleApproveFOB(fob)}
                                    style={{ padding:'4px 10px', background:'#16a34a', color:'white', border:'none', borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
                                    ✓ Approve
                                  </button>
                                )}
                                {fob.status==='Approved' && (
                                  <button onClick={()=>{
                                    fob.items?.forEach(item=>{
                                      const sup=suppliers.find(s=>s.name===item.supplier);
                                      if(sup?.contact?.email) window.open(`mailto:${sup.contact.email}?subject=Order Confirmation — ${fob.styleRef}&body=Dear ${sup.name},%0A%0AWe confirm your price for ${item.item} at $${item.price}/${item.unit}.%0AStyle: ${fob.styleRef} | Total FOB: $${fob.totalFob}%0A%0APlease confirm acceptance.`,'_blank');
                                    });
                                  }} style={{ padding:'4px 10px', background:'#7c3aed', color:'white', border:'none', borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
                                    ✉ Notify Suppliers
                                  </button>
                                )}
                                <button onClick={()=>setPoModal(fob)}
                                  style={{ padding:'4px 10px', background:'#2563eb', color:'white', border:'none', borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
                                  📄 Create PO
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}
            </div>
          );
        })()}

        {activeTab==='COSTING' && (()=>{
          const BLOCKS=[
            { key:'Fabric',       kw:['fabric','woven','knit','lining','yarn','denim','twill','poplin','jersey'] },
            { key:'Trims',        kw:['trim','button','zipper','label','tag','thread','elastic','interlining','velcro','hook'] },
            { key:'Embellishment',kw:['embroid','print','wash','heat transfer','patch','stone','sequin'] },
            { key:'CM',           kw:['cm','cut','make','sewing','stitching','labour'] },
            { key:'Packaging',    kw:['pack','polybag','carton','hanger','tissue','box','barcode'] },
            { key:'Other',        kw:[] }
          ];

          const getBlockQuotes=(block)=>{
            if(block.kw.length===0) return quotations.filter(q=>!BLOCKS.slice(0,-1).some(b=>b.kw.some(w=>(q.item||'').toLowerCase().includes(w))));
            return quotations.filter(q=>block.kw.some(w=>(q.item||'').toLowerCase().includes(w)));
          };

          const selectedBlockPrices=Object.entries(fobSelections).filter(([k,v])=>k.startsWith('BLOCK:')&&v);
          const blockTotal=selectedBlockPrices.reduce((a,[,q])=>a+parseFloat(q.price||0),0).toFixed(2);

          return (
            <div>
              <div style={{ marginBottom:'16px', padding:'12px 16px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'8px', display:'flex', gap:'10px', alignItems:'center' }}>
                <Info size={15} color="#2563eb"/>
                <span style={{ fontSize:'12px', color:'#1d4ed8' }}>
                  <b>Costing Integration:</b> Pick supplier-submitted prices for each costing block. Use <b>Push to Costing Module</b> to make these available inside CostingWorksheets as reference prices.
                </span>
              </div>

              {/* Invites + Received summary side by side */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
                <Section title="RFQ Invites Sent">
                  {invites.length===0
                    ? <p style={{ color:'#94a3b8', fontSize:'12px', textAlign:'center', padding:'15px' }}>No RFQs sent yet. Use Sourcing BOM tab.</p>
                    : <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
                        <thead><tr style={{ background:'#f1f5f9' }}>
                          {['Supplier','Item','Date','Status'].map(h=><th key={h} style={{ padding:'8px', textAlign:'left', color:'#475569' }}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {invites.slice(-12).reverse().map(i=>(
                            <tr key={i.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                              <td style={{ padding:'7px 8px', fontWeight:'600' }}>{i.supplier}</td>
                              <td style={{ padding:'7px 8px', color:'#64748b', fontSize:'11px' }}>{i.items}</td>
                              <td style={{ padding:'7px 8px', color:'#94a3b8', fontSize:'11px' }}>{i.date}</td>
                              <td style={{ padding:'7px 8px' }}><Tag label={i.status} color="#16a34a"/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  }
                </Section>

                <Section title="Quotes Received — Best Prices">
                  {quotations.length===0
                    ? <p style={{ color:'#94a3b8', fontSize:'12px', textAlign:'center', padding:'15px' }}>No quotes yet. Use Quotations tab to log.</p>
                    : <div>
                        {[...new Set(quotations.map(q=>q.item))].map(item=>{
                          const qs=quotations.filter(q=>q.item===item).sort((a,b)=>parseFloat(a.price)-parseFloat(b.price));
                          const best=qs[0];
                          return (
                            <div key={item} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid #f1f5f9', fontSize:'12px' }}>
                              <div>
                                <span style={{ fontWeight:'600', color:'#334155' }}>{item}</span>
                                <span style={{ marginLeft:'8px', fontSize:'10px', color:'#94a3b8' }}>{qs.length} quote{qs.length!==1?'s':''}</span>
                              </div>
                              <div style={{ textAlign:'right' }}>
                                <div style={{ color:'#16a34a', fontWeight:'bold' }}>${parseFloat(best.price).toFixed(2)} <span style={{ fontSize:'10px', color:'#94a3b8' }}>/{best.unit}</span></div>
                                <div style={{ fontSize:'10px', color:'#94a3b8' }}>{best.supplier}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                  }
                </Section>
              </div>

              {/* Block-wise price picker */}
              <Section title="Costing Block — Supplier Price Picker">
                {BLOCKS.map(block=>{
                  const bqs=getBlockQuotes(block);
                  return (
                    <div key={block.key} style={{ marginBottom:'20px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 12px', background:'#f8fafc', borderRadius:'6px', border:'1px solid #e2e8f0', marginBottom:'8px' }}>
                        <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#2563eb' }}/>
                        <span style={{ fontWeight:'700', fontSize:'13px', color:'#1e293b' }}>{block.key}</span>
                        <span style={{ fontSize:'11px', color:'#94a3b8', marginLeft:'auto' }}>{bqs.length} quote{bqs.length!==1?'s':''} available</span>
                      </div>
                      {bqs.length===0
                        ? <p style={{ color:'#94a3b8', fontSize:'11px', padding:'4px 12px' }}>No quotes for this block. Log quotes in Quotations tab.</p>
                        : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:'8px', paddingLeft:'20px' }}>
                            {bqs.map(q=>{
                              const selKey=`BLOCK:${block.key}:${q.item}`;
                              const sel=fobSelections[selKey]?.id===q.id;
                              const isBest=bqs.sort((a,b)=>parseFloat(a.price)-parseFloat(b.price))[0]?.id===q.id;
                              return (
                                <div key={q.id} onClick={()=>setFobSelections(prev=>({...prev,[selKey]:sel?undefined:q}))}
                                  style={{ border:`2px solid ${sel?'#16a34a':'#e2e8f0'}`, padding:'10px 14px', borderRadius:'8px', cursor:'pointer', background:sel?'#f0fdf4':'white', position:'relative', transition:'all 0.15s' }}>
                                  {isBest && <div style={{ position:'absolute', top:'-8px', right:'8px', background:'#16a34a', color:'white', fontSize:'9px', padding:'1px 6px', borderRadius:'8px', fontWeight:'700' }}>BEST</div>}
                                  {sel   && <div style={{ position:'absolute', top:'-8px', left:'8px',  background:'#2563eb', color:'white', fontSize:'9px', padding:'1px 6px', borderRadius:'8px', fontWeight:'700' }}>SELECTED</div>}
                                  <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b', marginBottom:'3px' }}>{q.item}</div>
                                  <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'5px' }}>{q.supplier}</div>
                                  <div style={{ fontSize:'16px', fontWeight:'800', color:sel?'#16a34a':'#2563eb' }}>
                                    ${parseFloat(q.price).toFixed(2)} <span style={{ fontSize:'10px', fontWeight:'400', color:'#94a3b8' }}>/{q.unit}</span>
                                  </div>
                                  {q.leadTime && <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'3px' }}>Lead: {q.leadTime}</div>}
                                  {q.moq      && <div style={{ fontSize:'10px', color:'#94a3b8' }}>MOQ: {q.moq}</div>}
                                </div>
                              );
                            })}
                          </div>
                      }
                    </div>
                  );
                })}

                {/* Push to Costing Module */}
                {selectedBlockPrices.length>0 && (
                  <div style={{ marginTop:'20px', padding:'16px', background:'#f0fdf4', borderRadius:'10px', border:'2px solid #16a34a' }}>
                    <div style={{ fontWeight:'700', fontSize:'13px', color:'#15803d', marginBottom:'12px' }}>📊 Selected Prices Summary</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(195px,1fr))', gap:'8px', marginBottom:'14px' }}>
                      {selectedBlockPrices.map(([key,q])=>{
                        const block=key.split(':')[1];
                        return (
                          <div key={key} style={{ background:'white', padding:'8px 12px', borderRadius:'6px', border:'1px solid #bbf7d0' }}>
                            <div style={{ fontSize:'10px', color:'#64748b', fontWeight:'700' }}>{block}</div>
                            <div style={{ fontSize:'12px', fontWeight:'700', color:'#334155' }}>{q.item}</div>
                            <div style={{ fontSize:'14px', fontWeight:'800', color:'#16a34a' }}>${parseFloat(q.price).toFixed(2)}</div>
                            <div style={{ fontSize:'10px', color:'#94a3b8' }}>{q.supplier}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                      <div style={{ fontSize:'16px', fontWeight:'800', color:'#15803d' }}>
                        Block Total: ${blockTotal}
                      </div>
                      <button onClick={()=>{
                        const prices=selectedBlockPrices.map(([key,q])=>({block:key.split(':')[1],...q}));
                        setDb(prev=>({...prev, ws:{...(prev.ws||{}), sourcingPrices:prices}}));
                        if(saveToDB) saveToDB('common',{type:'SOURCING_PRICES',data:prices});
                        alert('Supplier prices pushed to Costing Module ✓\nOpen Costing → each block will show reference prices.');
                      }} style={{ marginLeft:'auto', padding:'10px 22px', background:'#1e293b', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' }}>
                        📤 Push to Costing Module
                      </button>
                    </div>
                  </div>
                )}
              </Section>

              {/* Approved FOBs */}
              {savedFobs.filter(f=>f.status==='Approved').length>0 && (
                <Section title="Approved FOBs — Available in Costing Module">
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))', gap:'12px' }}>
                    {savedFobs.filter(f=>f.status==='Approved').map(fob=>(
                      <div key={fob.id} style={{ border:'1px solid #bbf7d0', background:'#f0fdf4', padding:'14px', borderRadius:'8px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
                          <span style={{ fontWeight:'700', fontSize:'13px', color:'#15803d' }}>{fob.styleRef||'—'}</span>
                          <Tag label="Approved" color="#16a34a"/>
                        </div>
                        <div style={{ fontSize:'22px', fontWeight:'800', color:'#16a34a', marginBottom:'6px' }}>${fob.totalFob}</div>
                        <div style={{ fontSize:'11px', color:'#64748b' }}>{fob.items?.length||0} items · {fob.date}</div>
                        <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'4px', marginBottom:'10px' }}>{fob.comment}</div>
                        <button onClick={()=>setPoModal(fob)}
                          style={{ width:'100%', padding:'6px', background:'#2563eb', color:'white', border:'none', borderRadius:'5px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
                          📄 Create PO
                        </button>
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          );
        })()}

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━ ALLOCATION ━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {activeTab==='ALLOCATION' && (()=>{

          // ── Capacity calculation helpers ────────────────────────────────
          const getSupCatCap = (supName, prodCat) => {
            const sup = suppliers.find(s=>s.name===supName);
            if (!sup) return 0;
            if (sup.catCapacities && prodCat && sup.catCapacities[prodCat])
              return parseFloat(sup.catCapacities[prodCat]) || 0;
            return parseFloat(sup.totalCapacity || 0);
          };

          // Monthly allocated qty per supplier per product category
          const getAllocatedByMonth = (supName, prodCat, monthIdx) => {
            return pos
              .filter(a => a.supplier === supName &&
                (!prodCat || !a.productCat || a.productCat === prodCat) &&
                a.startDate && new Date(a.startDate).getMonth() === monthIdx)
              .reduce((sum, a) => sum + (parseFloat(a.qty)||0), 0);
          };

          // Days required for a PO = qty / daily capacity
          const getDaysRequired = (qty, dailyCap) => dailyCap > 0 ? Math.ceil(parseFloat(qty||0) / dailyCap) : null;

          // Capacity utilisation per supplier
          const supUtilisation = suppliers.filter(s=>s.supplierType==='Garment Supplier').map(sup => {
            const supAllocs = pos.filter(a => a.supplier === sup.name);
            const totalAllocQty = supAllocs.reduce((s,a)=>s+(parseFloat(a.qty)||0), 0);
            const dailyCap = parseFloat(sup.totalCapacity || 0);
            const totalCapPcs = dailyCap * 26 * 12; // annual
            const utilPct = totalCapPcs > 0 ? Math.min(100,(totalAllocQty / totalCapPcs)*100) : 0;
            return { sup, supAllocs, totalAllocQty, dailyCap, utilPct };
          });

          // Planning module data for selected PO
          const planningData = poForm.poNo
            ? (db.planningAllocations || db.productionLogs || [])
                .filter(p => p && (p.poNo === poForm.poNo || p.styleRef === (poForm.poNo||'').split('—')[0]?.trim()))
            : [];

          // Over-capacity warning for new allocation
          const newAllocWarning = (() => {
            if (!poForm.supplier || !poForm.qty || !poForm.startDate) return null;
            const dailyCap = getSupCatCap(poForm.supplier, poForm.productCat);
            if (!dailyCap) return null;
            const monthIdx = new Date(poForm.startDate).getMonth();
            const alreadyAllocated = getAllocatedByMonth(poForm.supplier, poForm.productCat, monthIdx);
            const supCal = suppliers.find(s=>s.name===poForm.supplier)?.calendar?.[monthIdx];
            const workDays = parseFloat(supCal?.workingDays || 26);
            const monthCap = dailyCap * workDays;
            const newQty = parseFloat(poForm.qty || 0);
            const afterAlloc = alreadyAllocated + newQty;
            if (afterAlloc > monthCap) return {
              type: 'over',
              msg: `Over capacity! ${Math.round(afterAlloc).toLocaleString()} pcs allocated vs ${Math.round(monthCap).toLocaleString()} pcs capacity for ${MONTHS_SHORT[monthIdx]}`,
              pct: Math.round((afterAlloc/monthCap)*100)
            };
            const daysNeeded = getDaysRequired(newQty, dailyCap);
            return { type: 'ok', msg: `${daysNeeded} production days needed · ${Math.round((afterAlloc/monthCap)*100)}% of ${MONTHS_SHORT[monthIdx]} capacity used`, pct: Math.round((afterAlloc/monthCap)*100) };
          })();

          return (
            <div>
              {/* ── New Allocation Form ── */}
              <Section title="Allocate PO to Supplier">
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'10px', marginBottom:'14px' }}>
                  <div>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>PO NUMBER *</label>
                    <select value={poForm.poNo} onChange={e=>setPoForm({...poForm,poNo:e.target.value})}
                      style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px' }}>
                      <option value="">Select PO...</option>
                      {(db.mgmtStyles||[]).flatMap(s=>
                        (Array.isArray(s.pos)?s.pos:[]).map(p=>
                          (p&&typeof p==='object'&&p.num)
                            ? <option key={p.num} value={p.num}>{s.styleRef} — {p.num}</option>
                            : null
                        )
                      )}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>SUPPLIER *</label>
                    <select value={poForm.supplier} onChange={e=>setPoForm({...poForm,supplier:e.target.value})}
                      style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px' }}>
                      <option value="">Select Supplier...</option>
                      {suppliers.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>PRODUCT CATEGORY</label>
                    <select value={poForm.productCat||''} onChange={e=>setPoForm({...poForm,productCat:e.target.value})}
                      style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px' }}>
                      <option value="">All / General</option>
                      {poForm.supplier && suppliers.find(s=>s.name===poForm.supplier)?.productCats?.map((pc,i)=>(
                        <option key={i} value={pc.category}>{pc.category}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>ORDER QTY (pcs)</label>
                    <input type="number" value={poForm.qty||''} onChange={e=>setPoForm({...poForm,qty:e.target.value})}
                      placeholder="e.g. 5000"
                      style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px', boxSizing:'border-box' }}/>
                  </div>
                  <div>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>PRODUCTION START</label>
                    <input type="date" value={poForm.startDate||''} onChange={e=>setPoForm({...poForm,startDate:e.target.value})}
                      style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px', boxSizing:'border-box' }}/>
                  </div>
                  <div>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>EX-FACTORY DATE</label>
                    <input type="date" value={poForm.exFactoryDate||''} onChange={e=>setPoForm({...poForm,exFactoryDate:e.target.value})}
                      style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px', boxSizing:'border-box' }}/>
                  </div>
                  <div>
                    <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>STATUS</label>
                    <select value={poForm.status} onChange={e=>setPoForm({...poForm,status:e.target.value})}
                      style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px' }}>
                      {['Draft','Pending','Confirmed','In Production','Shipped','Delivered'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div style={{ display:'flex', alignItems:'flex-end' }}>
                    <button onClick={()=>{
                      if(newAllocWarning?.type==='over'){
                        if(!window.confirm(`⚠ ${newAllocWarning.msg}\n\nAllocate anyway?`)) return;
                      }
                      handleSavePO();
                    }}
                      style={{ width:'100%', padding:'9px', background:'#ea580c', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' }}>
                      + Allocate PO
                    </button>
                  </div>
                </div>

                {/* Capacity warning / confirmation */}
                {newAllocWarning && (
                  <div style={{ padding:'10px 14px', borderRadius:'6px', display:'flex', alignItems:'center', gap:'10px',
                    background:newAllocWarning.type==='over'?'#fef2f2':'#f0fdf4',
                    border:'1px solid '+(newAllocWarning.type==='over'?'#fecaca':'#bbf7d0') }}>
                    <span style={{ fontSize:'16px' }}>{newAllocWarning.type==='over'?'⚠️':'✅'}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'12px', fontWeight:'600', color:newAllocWarning.type==='over'?'#dc2626':'#16a34a' }}>{newAllocWarning.msg}</div>
                      <div style={{ height:'6px', background:'#e2e8f0', borderRadius:'3px', overflow:'hidden', marginTop:'5px' }}>
                        <div style={{ height:'100%', width:Math.min(100,newAllocWarning.pct)+'%',
                          background:newAllocWarning.pct>100?'#dc2626':newAllocWarning.pct>80?'#f59e0b':'#16a34a',
                          borderRadius:'3px', transition:'width 0.3s' }}/>
                      </div>
                    </div>
                    <span style={{ fontWeight:'800', fontSize:'14px', color:newAllocWarning.type==='over'?'#dc2626':'#16a34a' }}>{newAllocWarning.pct}%</span>
                  </div>
                )}

                {/* Planning module link */}
                {planningData.length > 0 && (
                  <div style={{ marginTop:'10px', padding:'10px', background:'#eff6ff', borderRadius:'6px', border:'1px solid #bfdbfe' }}>
                    <div style={{ fontSize:'11px', fontWeight:'700', color:'#1d4ed8', marginBottom:'6px' }}>📅 Planning Module — Linked Lines for this PO</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                      {planningData.slice(0,6).map((p,i)=>(
                        <span key={i} style={{ fontSize:'11px', background:'white', border:'1px solid #bfdbfe', padding:'3px 8px', borderRadius:'4px', color:'#1d4ed8' }}>
                          {p.line||p.lineNo||'Line ?'} · {p.qty||p.quantity||'?'} pcs · {p.date||'?'}
                        </span>
                      ))}
                      {planningData.length>6 && <span style={{ fontSize:'11px', color:'#94a3b8' }}>+{planningData.length-6} more</span>}
                    </div>
                  </div>
                )}
              </Section>

              {/* ── Supplier Capacity Utilisation ── */}
              {supUtilisation.filter(u=>u.supAllocs.length>0||u.dailyCap>0).length>0 && (
                <Section title="Supplier Capacity Utilisation">
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'14px' }}>
                    {supUtilisation.filter(u=>u.dailyCap>0).map(({sup,supAllocs,totalAllocQty,dailyCap,utilPct})=>(
                      <div key={sup.id} style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:'8px', padding:'14px', cursor:'pointer' }}
                        onClick={()=>setShowProfile(sup)}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
                          <div>
                            <div style={{ fontWeight:'700', color:'#1e293b', fontSize:'13px' }}>{sup.name}</div>
                            <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>
                              {Math.round(dailyCap).toLocaleString()} pcs/day ·  {supAllocs.length} PO{supAllocs.length!==1?'s':''}
                            </div>
                          </div>
                          <span style={{ fontWeight:'800', fontSize:'16px', color:utilPct>90?'#dc2626':utilPct>70?'#d97706':'#16a34a' }}>{utilPct.toFixed(0)}%</span>
                        </div>
                        <div style={{ height:'8px', background:'#f1f5f9', borderRadius:'4px', overflow:'hidden', marginBottom:'8px' }}>
                          <div style={{ height:'100%', width:Math.min(100,utilPct)+'%',
                            background:utilPct>90?'linear-gradient(to right,#ef4444,#dc2626)':utilPct>70?'linear-gradient(to right,#f59e0b,#d97706)':'linear-gradient(to right,#4ade80,#16a34a)',
                            borderRadius:'4px', transition:'width 0.4s' }}/>
                        </div>
                        {/* Per-category capacity breakdown */}
                        {sup.catCapacities && Object.keys(sup.catCapacities).length>0 && (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:'5px', marginBottom:'8px' }}>
                            {Object.entries(sup.catCapacities).map(([cat,cap])=>{
                              const catAlloc = supAllocs.filter(a=>!a.productCat||a.productCat===cat).reduce((s,a)=>s+(parseFloat(a.qty)||0),0);
                              const catCap26 = parseFloat(cap)*26;
                              const catPct = catCap26>0?Math.min(100,(catAlloc/catCap26)*100):0;
                              return (
                                <div key={cat} style={{ fontSize:'10px', padding:'3px 7px', borderRadius:'4px',
                                  background:catPct>90?'#fef2f2':catPct>70?'#fef3c7':'#f0fdf4',
                                  border:'1px solid '+(catPct>90?'#fecaca':catPct>70?'#fde68a':'#bbf7d0'),
                                  color:catPct>90?'#dc2626':catPct>70?'#d97706':'#16a34a', fontWeight:'600' }}>
                                  {cat}: {Math.round(cap).toLocaleString()} pcs/day
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Monthly mini-chart */}
                        <div>
                          <div style={{ fontSize:'10px', color:'#64748b', marginBottom:'4px', fontWeight:'600' }}>MONTHLY LOAD</div>
                          <div style={{ display:'flex', gap:'2px', height:'30px', alignItems:'flex-end' }}>
                            {MONTHS_SHORT.map((m,mi)=>{
                              const allocated = getAllocatedByMonth(sup.name, null, mi);
                              const supCal = sup.calendar?.[mi];
                              const workDays = parseFloat(supCal?.workingDays||26);
                              const monthCap = dailyCap * workDays;
                              const pct = monthCap>0?Math.min(100,(allocated/monthCap)*100):0;
                              return (
                                <div key={m} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'1px' }}>
                                  <div style={{ width:'100%', height:Math.max(2,pct*0.3)+'px',
                                    background:pct>90?'#dc2626':pct>70?'#f59e0b':'#2563eb',
                                    borderRadius:'1px 1px 0 0' }} title={`${m}: ${Math.round(allocated).toLocaleString()} pcs allocated of ${Math.round(monthCap).toLocaleString()}`}/>
                                </div>
                              );
                            })}
                          </div>
                          <div style={{ display:'flex', gap:'2px' }}>
                            {MONTHS_SHORT.map(m=><div key={m} style={{ flex:1, fontSize:'8px', color:'#94a3b8', textAlign:'center' }}>{m[0]}</div>)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* ── Allocation List ── */}
              <Section title="All Allocations" actions={<span style={{ fontSize:'11px', color:'#94a3b8' }}>{pos.length} records</span>}>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                    <thead>
                      <tr style={{ background:'#1e293b', color:'white' }}>
                        {['PO No','Supplier','Product Cat','Qty (pcs)','Start','Ex-Factory','Status','Days Reqd','Cap Used','Actions'].map(h=>(
                          <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:'700', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pos.length===0 && (
                        <tr><td colSpan={10} style={{ padding:'30px', textAlign:'center', color:'#94a3b8' }}>No allocations yet</td></tr>
                      )}
                      {pos.map(a=>{
                        const sup = suppliers.find(s=>s.name===a.supplier);
                        const dailyCap = getSupCatCap(a.supplier, a.productCat);
                        const daysReqd = getDaysRequired(a.qty, dailyCap);
                        const monthIdx = a.startDate ? new Date(a.startDate).getMonth() : null;
                        const monthAlloc = monthIdx!==null ? getAllocatedByMonth(a.supplier, a.productCat, monthIdx) : 0;
                        const supCal = sup?.calendar?.[monthIdx];
                        const workDays = parseFloat(supCal?.workingDays||26);
                        const monthCap = dailyCap * workDays;
                        const capPct = monthCap>0 ? Math.min(150,(monthAlloc/monthCap)*100) : 0;
                        const isOver = capPct > 100;
                        return (
                          <tr key={a.id} style={{ borderBottom:'1px solid #f1f5f9', background:isOver?'#fff8f8':'white' }}>
                            <td style={{ padding:'10px 12px', fontWeight:'700', color:'#1e293b' }}>{a.poNo||a.po}</td>
                            <td style={{ padding:'10px 12px' }}>
                              <button onClick={()=>sup&&setShowProfile(sup)}
                                style={{ background:'none', border:'none', color:sup?'#2563eb':'#334155', cursor:sup?'pointer':'default', fontWeight:'600', fontSize:'12px', padding:0, textDecoration:sup?'underline':'none' }}>
                                {a.supplier}
                              </button>
                            </td>
                            <td style={{ padding:'10px 12px', color:'#64748b', fontSize:'11px' }}>{a.productCat||'—'}</td>
                            <td style={{ padding:'10px 12px', fontWeight:'700', color:'#1e293b' }}>{a.qty ? parseFloat(a.qty).toLocaleString() : '—'}</td>
                            <td style={{ padding:'10px 12px', color:'#64748b' }}>{a.startDate||'—'}</td>
                            <td style={{ padding:'10px 12px', color:'#64748b' }}>{a.exFactoryDate||'—'}</td>
                            <td style={{ padding:'10px 12px' }}>
                              <span style={{ padding:'2px 8px',
                                background:a.status==='Confirmed'?'#dcfce7':a.status==='In Production'?'#dbeafe':a.status==='Shipped'?'#fef9c3':'#f1f5f9',
                                color:a.status==='Confirmed'?'#16a34a':a.status==='In Production'?'#2563eb':a.status==='Shipped'?'#d97706':'#64748b',
                                borderRadius:'12px', fontSize:'11px', fontWeight:'600' }}>
                                {a.status}
                              </span>
                            </td>
                            <td style={{ padding:'10px 12px', color:daysReqd?'#334155':'#94a3b8', fontWeight:'600' }}>
                              {daysReqd ? daysReqd+' d' : '—'}
                            </td>
                            <td style={{ padding:'10px 12px' }}>
                              {monthCap>0
                                ? <div style={{ display:'flex', alignItems:'center', gap:'6px', minWidth:'90px' }}>
                                    <div style={{ flex:1, height:'5px', background:'#f1f5f9', borderRadius:'3px', overflow:'hidden' }}>
                                      <div style={{ height:'100%', width:Math.min(100,capPct)+'%', background:capPct>100?'#dc2626':capPct>80?'#f59e0b':'#16a34a', borderRadius:'3px' }}/>
                                    </div>
                                    <span style={{ fontSize:'10px', fontWeight:'700', color:capPct>100?'#dc2626':capPct>80?'#d97706':'#16a34a', whiteSpace:'nowrap' }}>
                                      {capPct.toFixed(0)}%{isOver?' ⚠':''}
                                    </span>
                                  </div>
                                : <span style={{ fontSize:'11px', color:'#94a3b8' }}>—</span>
                              }
                            </td>
                            <td style={{ padding:'10px 12px' }}>
                              <div style={{ display:'flex', gap:'5px' }}>
                                <button onClick={()=>{
                                  const fob=savedFobs.find(f=>f.styleRef&&(a.poNo||'').includes(f.styleRef));
                                  setPoModal(fob||{styleRef:a.poNo,supplier:a.supplier,totalFob:'0.00',items:[]});
                                }} style={{ padding:'4px 8px', background:'#2563eb', color:'white', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer', fontWeight:'600' }}>
                                  📄 PO
                                </button>
                                <button onClick={()=>deleteItem(pos,setPos,a.id,'allocations')}
                                  style={{ padding:'4px 8px', background:'#fee2e2', color:'#ef4444', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer' }}>
                                  Del
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Section>
            </div>
          );
        })()}

        {/* ━━━━━━━━━━━━━━━━━━━━━━━ MARKET RATES ━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {activeTab==='MARKET RATES' && (
          <div>
            <Section title="Best Price Comparison — All Items">
              {products.length===0&&quotations.length===0
                ? <p style={{ color:'#94a3b8', textAlign:'center', padding:'30px' }}>Add products and log quotations to see market rate comparison.</p>
                : <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                      <thead>
                        <tr style={{ background:'#1e293b', color:'white' }}>
                          {['Item','Best Price','Supplier','Unit','MOQ','Lead Time','# Quotes','Spread'].map(h=>(
                            <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:'700' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...new Set([...products.map(p=>p.name),...quotations.map(q=>q.item)])].map(item=>{
                          const relP=products.filter(p=>p.name===item);
                          const relQ=quotations.filter(q=>q.item===item);
                          const allPrices=[...relP.map(p=>({price:p.rate,supplier:p.supplier,unit:p.unit,moq:p.moq,leadTime:p.leadTime})),...relQ.map(q=>({price:q.price,supplier:q.supplier,unit:q.unit,moq:q.moq,leadTime:q.leadTime}))].filter(x=>parseFloat(x.price)>0).sort((a,b)=>parseFloat(a.price)-parseFloat(b.price));
                          if(allPrices.length===0) return null;
                          const best=allPrices[0];
                          const worst=allPrices[allPrices.length-1];
                          const spread=allPrices.length>1?(((parseFloat(worst.price)-parseFloat(best.price))/parseFloat(best.price))*100).toFixed(0):0;
                          return (
                            <tr key={item} style={{ borderBottom:'1px solid #e2e8f0' }}>
                              <td style={{ padding:'10px 14px', fontWeight:'700', color:'#1e293b' }}>{item}</td>
                              <td style={{ padding:'10px 14px', fontWeight:'bold', color:'#16a34a', fontSize:'14px' }}>${parseFloat(best.price).toFixed(2)}</td>
                              <td style={{ padding:'10px 14px', color:'#334155' }}>{best.supplier}</td>
                              <td style={{ padding:'10px 14px', color:'#64748b' }}>{best.unit||'-'}</td>
                              <td style={{ padding:'10px 14px', color:'#64748b' }}>{best.moq||'-'}</td>
                              <td style={{ padding:'10px 14px', color:'#64748b' }}>{best.leadTime||'-'}</td>
                              <td style={{ padding:'10px 14px', textAlign:'center' }}>
                                <span style={{ background:'#dbeafe', color:'#2563eb', borderRadius:'12px', padding:'2px 8px', fontWeight:'bold', fontSize:'11px' }}>{allPrices.length}</span>
                              </td>
                              <td style={{ padding:'10px 14px' }}>
                                {allPrices.length>1
                                  ? <span style={{ background:spread>30?'#fee2e2':spread>10?'#fef3c7':'#dcfce7', color:spread>30?'#ef4444':spread>10?'#d97706':'#16a34a', borderRadius:'12px', padding:'2px 8px', fontWeight:'bold', fontSize:'11px' }}>
                                      {spread}% range
                                    </span>
                                  : <span style={{ color:'#94a3b8', fontSize:'11px' }}>Single quote</span>
                                }
                              </td>
                            </tr>
                          );
                        }).filter(Boolean)}
                      </tbody>
                    </table>
                  </div>
              }
            </Section>

            {/* Supplier-wise product summary */}
            {suppliers.length>0 && (
              <Section title="Supplier-wise Product Catalogue Summary">
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'12px' }}>
                  {suppliers.map(sup=>{
                    const supProducts=[...products.filter(p=>p.supplier===sup.name),...quotations.filter(q=>q.supplier===sup.name)];
                    if(supProducts.length===0) return null;
                    return (
                      <div key={sup.id} style={{ border:'1px solid #e2e8f0', padding:'14px', borderRadius:'8px', background:'white' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
                          <div style={{ fontWeight:'700', fontSize:'13px', color:'#1e293b' }}>{sup.name}</div>
                          <Tag label={sup.supplierType?.split(' ')[0]} color="#2563eb"/>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                          {supProducts.slice(0,4).map((p,i)=>(
                            <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'11px' }}>
                              <span style={{ color:'#64748b' }}>{p.name||p.item}</span>
                              <span style={{ color:'#16a34a', fontWeight:'bold' }}>${parseFloat(p.rate||p.price||0).toFixed(2)}</span>
                            </div>
                          ))}
                          {supProducts.length>4 && <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'3px' }}>+{supProducts.length-4} more items</div>}
                        </div>
                        <button onClick={()=>setShowProfile(sup)}
                          style={{ marginTop:'10px', width:'100%', padding:'5px', background:'#f1f5f9', color:'#334155', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'11px', cursor:'pointer', fontWeight:'600' }}>
                          View Full Profile
                        </button>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              </Section>
            )}
          </div>
        )}

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━ PO MODAL ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {poModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
            onClick={e=>e.target===e.currentTarget&&setPoModal(null)}>
            <div style={{ background:'white', borderRadius:'12px', width:'100%', maxWidth:'720px', maxHeight:'90vh', overflow:'auto', boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }}>
              {/* PO Header */}
              <div style={{ background:'linear-gradient(135deg,#1e293b,#334155)', padding:'18px 24px', borderRadius:'12px 12px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:'800', color:'white', fontSize:'16px' }}>📄 Purchase Order</div>
                  <div style={{ color:'#94a3b8', fontSize:'12px', marginTop:'2px' }}>{poModal.styleRef||'Draft PO'} · {new Date().toLocaleDateString()}</div>
                </div>
                <button onClick={()=>setPoModal(null)} style={{ background:'none', border:'none', color:'white', cursor:'pointer' }}><X size={18}/></button>
              </div>

              <div style={{ padding:'24px' }}>
                {/* PO Meta */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px', marginBottom:'20px', padding:'16px', background:'#f8fafc', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
                  <div>
                    <div style={{ fontSize:'10px', color:'#94a3b8', fontWeight:'700', marginBottom:'4px', textTransform:'uppercase' }}>PO Number</div>
                    <div style={{ fontSize:'14px', fontWeight:'800', color:'#1e293b' }}>PO-{Date.now().toString().slice(-6)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'10px', color:'#94a3b8', fontWeight:'700', marginBottom:'4px', textTransform:'uppercase' }}>Style Ref</div>
                    <div style={{ fontSize:'14px', fontWeight:'700', color:'#1e293b' }}>{poModal.styleRef||'-'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:'10px', color:'#94a3b8', fontWeight:'700', marginBottom:'4px', textTransform:'uppercase' }}>Date</div>
                    <div style={{ fontSize:'14px', fontWeight:'700', color:'#1e293b' }}>{new Date().toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Supplier info */}
                {(()=>{
                  const primarySup=poModal.items?.[0]?.supplier||poModal.supplier;
                  const supObj=suppliers.find(s=>s.name===primarySup);
                  return supObj ? (
                    <>
                      <div style={{ marginBottom:'16px', padding:'14px', border:'1px solid #e2e8f0', borderRadius:'8px' }}>
                        <div style={{ fontSize:'11px', color:'#94a3b8', fontWeight:'700', marginBottom:'8px', textTransform:'uppercase' }}>Supplier Details</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'12px' }}>
                          <div><b>{supObj.name}</b></div>
                          <div>{supObj.address?.city}, {supObj.address?.country}</div>
                          <div>{supObj.contact?.email||'-'}</div>
                          <div>{supObj.contact?.phone||'-'}</div>
                          <div>Tax ID: {supObj.taxId||'-'}</div>
                          <div>Type: {supObj.supplierType}</div>
                        </div>
                      </div>
                      {/* ── Wastage Alert ── */}
                      {(()=>{
                        const agreedW = parseFloat(supObj.agreedWastage||0);
                        if (agreedW <= 0) return null;
                        // Find permissible wastage from any fabric/trim in db
                        const allItems = [...(db?.fabrics||[]), ...(db?.trims||[])];
                        const permItems = allItems.filter(it => it.wastage > 0);
                        // Compare against each item in PO
                        const alerts = (poModal.items||[]).map(poItem => {
                          const matched = permItems.find(it => (poItem.item||'').toLowerCase().includes((it.name||'').toLowerCase()));
                          if (!matched) return null;
                          const permW = parseFloat(matched.wastage||0);
                          const diff = agreedW - permW;
                          if (Math.abs(diff) < 0.01) return null;
                          return { item: poItem.item, permW, agreedW, diff };
                        }).filter(Boolean);
                        if (alerts.length === 0 && agreedW > 0) {
                          return (
                            <div style={{ marginBottom:'12px', padding:'12px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:'8px', fontSize:'12px', color:'#92400e' }}>
                              <b>📦 Wastage Notice:</b> Supplier agreed wastage is <b>{agreedW}%</b>. Compare with Global Masters permissible wastage when ordering.
                            </div>
                          );
                        }
                        return alerts.map((a,i)=>(
                          <div key={i} style={{ marginBottom:'8px', padding:'12px', background: a.diff>0?'#fef3c7':'#f0fdf4', border:`1px solid ${a.diff>0?'#fde68a':'#86efac'}`, borderRadius:'8px', fontSize:'12px', color: a.diff>0?'#92400e':'#166534' }}>
                            {a.diff>0 ? (
                              <>⚠️ <b>{a.item}:</b> Supplier agrees to supply <b>{a.agreedW}% extra</b>, but your permissible wastage is only <b>{a.permW}%</b>. <b>Order {a.diff.toFixed(1)}% LESS</b> than planned to avoid excess inventory.</>
                            ) : (
                              <>✅ <b>{a.item}:</b> Supplier agreed wastage ({a.agreedW}%) is <b>{Math.abs(a.diff).toFixed(1)}% below</b> your permissible ({a.permW}%). You may need to <b>order {Math.abs(a.diff).toFixed(1)}% more</b> to cover production wastage.</>
                            )}
                          </div>
                        ));
                      })()}
                    </>
                  ) : null;
                })()}

                {/* Line items */}
                {poModal.items?.length>0 && (
                  <div style={{ marginBottom:'16px' }}>
                    <div style={{ fontSize:'11px', color:'#94a3b8', fontWeight:'700', marginBottom:'8px', textTransform:'uppercase' }}>Order Line Items</div>
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                        <thead>
                          <tr style={{ background:'#f1f5f9' }}>
                            {['Item','Supplier','Unit Price','Unit','Lead Time'].map(h=>(
                              <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#475569', fontWeight:'700' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {poModal.items.map((item,i)=>(
                            <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                              <td style={{ padding:'8px 10px', fontWeight:'600' }}>{item.item}</td>
                              <td style={{ padding:'8px 10px', color:'#64748b' }}>{item.supplier}</td>
                              <td style={{ padding:'8px 10px', color:'#16a34a', fontWeight:'bold' }}>${parseFloat(item.price||0).toFixed(2)}</td>
                              <td style={{ padding:'8px 10px', color:'#64748b' }}>{item.unit||'-'}</td>
                              <td style={{ padding:'8px 10px', color:'#64748b' }}>{item.leadTime||'-'}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ borderTop:'2px solid #e2e8f0', background:'#f8fafc' }}>
                            <td colSpan={2} style={{ padding:'10px', fontWeight:'bold', color:'#334155' }}>Total FOB</td>
                            <td colSpan={3} style={{ padding:'10px', fontWeight:'800', color:'#16a34a', fontSize:'16px' }}>${poModal.totalFob}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Comments */}
                {poModal.comment && (
                  <div style={{ marginBottom:'16px', padding:'12px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:'6px', fontSize:'12px', color:'#92400e' }}>
                    <b>Notes:</b> {poModal.comment}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display:'flex', gap:'10px' }}>
                  <button onClick={()=>{
                    const sup=suppliers.find(s=>s.name===(poModal.items?.[0]?.supplier||poModal.supplier));
                    if(sup?.contact?.email){
                      const body=`Dear ${sup.name},%0A%0APO Confirmation — Style: ${poModal.styleRef||'-'}%0ATotal FOB: $${poModal.totalFob}%0A%0AItems:%0A${(poModal.items||[]).map(i=>`• ${i.item}: $${i.price}/${i.unit}`).join('%0A')}%0A%0APlease confirm acceptance.`;
                      window.open(`mailto:${sup.contact.email}?subject=PO Confirmation — ${poModal.styleRef}&body=${body}`,'_blank');
                    } else { alert('No email address for this supplier. Update in Registration tab.'); }
                  }} style={{ flex:1, padding:'10px', background:'#2563eb', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' }}>
                    ✉ Send PO via Email
                  </button>
                  <button onClick={()=>{
                    const sup=suppliers.find(s=>s.name===(poModal.items?.[0]?.supplier||poModal.supplier));
                    const ph=(sup?.contact?.whatsapp||sup?.contact?.mobile||'').replace(/\D/g,'');
                    const msg=`*PO Confirmation*\nStyle: ${poModal.styleRef||'-'}\nTotal FOB: $${poModal.totalFob}\n\nItems:\n${(poModal.items||[]).map(i=>`• ${i.item}: $${i.price}/${i.unit}`).join('\n')}\n\nPlease confirm acceptance.`;
                    if(ph) window.open(`https://wa.me/${ph}?text=${encodeURIComponent(msg)}`,'_blank');
                    else alert('No WhatsApp number for this supplier. Update in Registration tab.');
                  }} style={{ flex:1, padding:'10px', background:'#16a34a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' }}>
                    💬 Send PO via WhatsApp
                  </button>
                  <button onClick={()=>{
                    handleSavePO();
                    setPoModal(null);
                  }} style={{ padding:'10px 20px', background:'#1e293b', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' }}>
                    💾 Save PO
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}/* end component */
